import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { KTSVG } from '../../../_metronic/helpers'
import { toCdnUrl, watermarkPreviewSrc } from '../../utils/cdnUrl'
import { getTodaySchedule, getScheduleByDate, getSlotDetails, postNow, resetError, resetAllTodayErrors, resetAllTodayErrorsSystemWide, resetGetGroups, resetJoinGroup, deleteAllTodayJoinGroups, joinGroupNow, rescheduleAllCampaigns, getReschedulePreview, setDuplicatePostsMode, verifyPost, updateMaxJoinGroups, updateProfilePostsPerDay, updateProfileJoinCapOverride,
  updateAllProfilesJoinCapOverride, joinCampaignGroups, joinCampaignGroupsForProfile, joinCampaignGroupsForProfilesBulk, adminAddProfile, adminSoftDeleteProfile, clearScheduleDay,
  getProfileBlacklistedGroups, unblacklistProfileGroup, clearProfileBlacklist, BlacklistedGroup, ScheduleSlot, ScheduleStats, ProfileStats, ReschedulePreviewResponse } from './core/_requests'
import { CreateFromPostModal } from '../marketplace/CreateFromPostModal'
import axios from 'axios'

// YYYY-MM-DD of today in Israel (toISOString is UTC and gives yesterday between 00:00-03:00 IL)
const israelToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Jerusalem' })

const API_URL = process.env.REACT_APP_API_URL

// A slot flagged is_error whose message starts with "warmup_skipped" is an
// intentional warm-up throttle (a recently-unblocked profile posts only a
// fraction of its slots while it ramps back up), NOT a real failure. Render it
// as its own state so the grid stops reading these as errors.
const isWarmupSkip = (s: { is_error?: boolean; error_message?: string }): boolean =>
  !!s.is_error && !!s.error_message && s.error_message.startsWith('warmup_skipped')

// FB hard-block (error 1390008 — "account temporarily blocked"). The code lives
// in the Log JSON, so the server joins it onto the slot as fb_hard_blocked.
const isHardBlocked = (s: { fb_hard_blocked?: boolean }): boolean => !!s.fb_hard_blocked

// The post that actually TRIPPED the limit: FB returned a rate-limit/"you can't
// post" response to this attempt (error_message starts with "rate_limit"). This
// is the slot that caused the profile to be limited — mark it specially, distinct
// from the warm-up skips that merely follow it.
const isLimitHit = (s: { is_error?: boolean; error_message?: string }): boolean =>
  !!s.is_error && !!s.error_message && s.error_message.startsWith('rate_limit')

const TodaySchedule: React.FC = () => {
  const { slotId } = useParams<{ slotId?: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedProfileId = searchParams.get('profile') || ''
  const [hourlySlots, setHourlySlots] = useState<Record<number, ScheduleSlot[]>>({})
  const [multiDaySlots, setMultiDaySlots] = useState<Record<string, Record<number, ScheduleSlot[]>>>({})
  const [multiDayStats, setMultiDayStats] = useState<Record<string, ScheduleStats>>({})
  const [stats, setStats] = useState<ScheduleStats | null>(null)
  const [profileStats, setProfileStats] = useState<ProfileStats[]>([])
  const [requiresProfile, setRequiresProfile] = useState(false) // heavy account: grid loads per-profile
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState<string>(israelToday())
  const [selectedSlot, setSelectedSlot] = useState<ScheduleSlot | null>(null)
  const [slotDetails, setSlotDetails] = useState<any>(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [postingNow, setPostingNow] = useState(false)
  const [postNowError, setPostNowError] = useState<string | null>(null)
  const [postNowSuccess, setPostNowSuccess] = useState<string | null>(null)
  const [postUrl, setPostUrl] = useState<string | null>(null)
  const [showMarketplaceModal, setShowMarketplaceModal] = useState(false)
  const [deletingJoinGroups, setDeletingJoinGroups] = useState(false)
  const [resettingJoinGroup, setResettingJoinGroup] = useState(false)
  const [joiningGroupNow, setJoiningGroupNow] = useState(false)
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)
  const [reschedulePreview, setReschedulePreview] = useState<ReschedulePreviewResponse | null>(null)
  const [loadingReschedulePreview, setLoadingReschedulePreview] = useState(false)
  const [rescheduling, setRescheduling] = useState(false)
  const [rescheduleResult, setRescheduleResult] = useState<string | null>(null)
  const [togglingDuplicateMode, setTogglingDuplicateMode] = useState(false)
  // Join-groups-for-profiles modal (bulk: many profiles × many campaigns)
  const [showJoinForProfile, setShowJoinForProfile] = useState(false)
  const [jfpProfiles, setJfpProfiles] = useState<string[]>([])
  const [jfpCampaignIds, setJfpCampaignIds] = useState<string[]>([])
  const [jfpCount, setJfpCount] = useState(3)
  const [jfpBusy, setJfpBusy] = useState(false)
  const [jfpCampaigns, setJfpCampaigns] = useState<{_id: string; name: string}[]>([])
  // Admin viewing as the user (admin_switch in the JWT) — gates the duplicate-mode toggle
  const isAdminSwitch = useMemo(() => {
    try {
      const raw = localStorage.getItem('kt-auth-react-v')
      const token = raw ? JSON.parse(raw)?.api_token : null
      if (!token) return false
      const payload = JSON.parse(atob(token.split('.')[1]))
      return !!payload?.user?.admin_switch
    } catch (_) { return false }
  }, [])
  const [verifyingPost, setVerifyingPost] = useState(false)
  const [verifyPostResult, setVerifyPostResult] = useState<{ success: boolean; message: string } | null>(null)
  const [editingPostsPerDay, setEditingPostsPerDay] = useState<{profileId: string; value: string} | null>(null)
  const [savedPostsPerDay, setSavedPostsPerDay] = useState<string | null>(null)
  // Admin: per-profile override of the global 7/day group-join cap
  const [joinCapModal, setJoinCapModal] = useState<ProfileStats | null>(null)
  // Per-profile group blacklist popup (the bot's cannot_post/group_unavailable list)
  const [blacklistModal, setBlacklistModal] = useState<ProfileStats | null>(null)
  const [blacklistGroups, setBlacklistGroups] = useState<BlacklistedGroup[]>([])
  const [blacklistLoading, setBlacklistLoading] = useState(false)
  const [blacklistCanEdit, setBlacklistCanEdit] = useState(false)
  const [blacklistBusy, setBlacklistBusy] = useState<string | null>(null) // group id, or 'all'
  const [blacklistError, setBlacklistError] = useState<string | null>(null)
  const [joinCapValue, setJoinCapValue] = useState('')
  const [joinCapBusy, setJoinCapBusy] = useState(false)
  const [joinCapError, setJoinCapError] = useState<string | null>(null)
  // Bulk (whole-account) join cap — the popup opened from the toolbar.
  const [bulkJoinCapOpen, setBulkJoinCapOpen] = useState(false)
  const [bulkJoinCapValue, setBulkJoinCapValue] = useState('')
  const [bulkJoinCapBusy, setBulkJoinCapBusy] = useState(false)
  const [bulkJoinCapError, setBulkJoinCapError] = useState<string | null>(null)
  const [bulkJoinCapDone, setBulkJoinCapDone] = useState<string | null>(null)
  // Per-row edits inside that popup: profileId -> typed value (unsaved until pressed).
  const [rowJoinCap, setRowJoinCap] = useState<Record<string, string>>({})
  const [rowJoinCapBusy, setRowJoinCapBusy] = useState<string | null>(null)

  const saveRowJoinCap = async (profileId: string, value: string) => {
    const trimmed = String(value || '').trim()
    const num = trimmed === '' ? null : parseInt(trimmed, 10)
    if (num !== null && (!Number.isFinite(num) || num < 1 || num > 100)) {
      setBulkJoinCapError('הזן מספר בין 1 ל-100')
      return
    }
    setRowJoinCapBusy(profileId)
    setBulkJoinCapError(null)
    try {
      const res = await updateProfileJoinCapOverride(profileId, num)
      if (res.success) {
        // Reflect the saved value locally so the row stops showing as unsaved without
        // waiting for a full reload of the schedule.
        setProfileStats((prev) => prev.map((p) => p._id === profileId
          ? { ...p, join_cap_override: res.join_cap_override || 0, effective_join_cap: res.effective_join_cap }
          : p))
        setRowJoinCap((prev) => { const n = { ...prev }; delete n[profileId]; return n })
        setBulkJoinCapDone(num === null ? 'המכסה הידנית בוטלה לפרופיל' : `נשמרה מכסה של ${num} ליום לפרופיל`)
      } else {
        setBulkJoinCapError(res.error || 'העדכון נכשל')
      }
    } catch (e: any) {
      setBulkJoinCapError(e?.response?.data?.error || 'העדכון נכשל')
    }
    setRowJoinCapBusy(null)
  }

  // Apply to every profile at once. `clear` removes the override and puts the account
  // back on the normal rules (7/day steady state, 30/day while ramping up).
  const applyBulkJoinCap = async (clear: boolean) => {
    const num = clear ? null : parseInt(bulkJoinCapValue, 10)
    if (!clear && (!Number.isFinite(num as number) || (num as number) < 1 || (num as number) > 100)) {
      setBulkJoinCapError('הזן מספר בין 1 ל-100')
      return
    }
    setBulkJoinCapBusy(true)
    setBulkJoinCapError(null)
    try {
      const res = await updateAllProfilesJoinCapOverride(num)
      if (res.success) {
        setRowJoinCap({})
        setBulkJoinCapDone(clear
          ? `בוטלה המכסה הידנית עבור ${res.profiles_updated ?? 0} פרופילים`
          : `נקבעה מכסה של ${res.join_cap_override} ליום עבור ${res.profiles_updated ?? 0} פרופילים`)
        loadData()
      } else {
        setBulkJoinCapError(res.error || 'העדכון נכשל')
      }
    } catch (e: any) {
      setBulkJoinCapError(e?.response?.data?.error || 'העדכון נכשל')
    }
    setBulkJoinCapBusy(false)
  }
  // (date|hour) keys for join-group cells the user has chosen to expand fully.
  // Default: collapsed past the JOIN_GROUP_CAP to keep DOM size manageable —
  // some accounts have thousands of join-group slots which hung the page.
  const [expandedJoinCells, setExpandedJoinCells] = useState<Set<string>>(new Set())
  const JOIN_GROUP_CAP = 20
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('')
  const [joiningCampaignGroups, setJoiningCampaignGroups] = useState<string | null>(null)
  const [joinCampaignGroupsInput, setJoinCampaignGroupsInput] = useState<Record<string, string>>({})
  const [resettingAllErrors, setResettingAllErrors] = useState(false)
  const [resettingAllErrorsSystemWide, setResettingAllErrorsSystemWide] = useState(false)
  const [resetSystemWideResult, setResetSystemWideResult] = useState<string | null>(null)
  const isAdminMode = typeof window !== 'undefined' && !!localStorage.getItem('admin_auth_token')
  // Admin managing this client's profiles — either "switched into" the account
  // (admin_switch in the JWT) or logged into the admin panel (admin_auth_token,
  // sent as X-Admin-Token). The backend accepts either.
  const canAdminProfiles = isAdminSwitch || isAdminMode
  const [resettingGetGroups, setResettingGetGroups] = useState(false)
  const [resetGetGroupsResult, setResetGetGroupsResult] = useState<string | null>(null)
  const [requestLogStats, setRequestLogStats] = useState<Record<string, {total_requests: number; total_errors: number; total_rate_limited: number; request_counts: Record<string, number>}>>({})
  const currentHour = new Date().getHours()

  const CAMPAIGN_COLORS = [
    '#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6',
    '#8b5cf6', '#ef4444', '#14b8a6', '#f97316', '#06b6d4',
    '#84cc16', '#e879f9', '#fb923c', '#22d3ee', '#a78bfa',
  ]

  // Build campaign color map from all slots for the selected profile
  const campaignColorMap = useMemo(() => {
    const map = new Map<string, string>()
    if (!selectedProfileId) return map
    const counts = new Map<string, number>()
    for (let h = 0; h < 24; h++) {
      (hourlySlots[h] || []).forEach(s => {
        if (s.user?._id === selectedProfileId && s.slot_type !== 'join_group') {
          const cId = s.campaign?._id || 'unknown'
          counts.set(cId, (counts.get(cId) || 0) + 1)
        }
      })
    }
    // Sort by count descending for consistent color assignment
    const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
    sorted.forEach(([cId], i) => map.set(cId, CAMPAIGN_COLORS[i % CAMPAIGN_COLORS.length]))
    return map
  }, [hourlySlots, selectedProfileId])

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedProfileId])

  // Inject the flashing-red animation used to make FB hard-block (1390008)
  // markers impossible to miss. Runs once; no-op if already present.
  useEffect(() => {
    const id = 'zipper-flash-style'
    if (document.getElementById(id)) return
    const el = document.createElement('style')
    el.id = id
    el.innerHTML = `
      @keyframes zipperBlinkBg { 0%,100%{ background-color:#dc3545; color:#fff; } 50%{ background-color:#3a0007; color:#fff; } }
      .zipper-blink-bg { animation: zipperBlinkBg .8s steps(1,end) infinite; }
      @keyframes zipperFlashRing { 0%,100%{ box-shadow:0 0 0 0 rgba(220,53,69,.85);} 50%{ box-shadow:0 0 0 5px rgba(220,53,69,0);} }
      .zipper-flash-ring { animation: zipperFlashRing 1s ease-out infinite; border-radius:50%; }
    `
    document.head.appendChild(el)
  }, [])

  // Open modal from URL param
  useEffect(() => {
    if (slotId && !loading) {
      // Find the slot in hourlySlots
      let foundSlot: ScheduleSlot | null = null
      for (const hour in hourlySlots) {
        const slot = hourlySlots[hour].find(s => s._id === slotId)
        if (slot) {
          foundSlot = slot
          break
        }
      }
      if (foundSlot) {
        handleSlotClick(foundSlot)
      } else {
        // Slot not found in current date, try to load details directly
        loadSlotFromUrl(slotId)
      }
    }
  }, [slotId, loading, hourlySlots])

  const loadSlotFromUrl = async (id: string) => {
    try {
      setLoadingDetails(true)
      const response = await getSlotDetails(id)
      if (response.slot) {
        setSelectedSlot(response.slot as ScheduleSlot)
        setSlotDetails(response.slot)
      }
    } catch (err) {
      console.error('Error loading slot from URL:', err)
    } finally {
      setLoadingDetails(false)
    }
  }

  // Generate array of 5 dates ending at selectedDate
  const getDayRange = (endDate: string): string[] => {
    const days: string[] = []
    const end = new Date(endDate + 'T12:00:00')
    for (let i = 4; i >= 0; i--) {
      const d = new Date(end)
      d.setDate(d.getDate() - i)
      days.push(d.toISOString().split('T')[0])
    }
    return days
  }

  const dayRange = useMemo(() => getDayRange(selectedDate), [selectedDate])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      const todayStr = israelToday()

      // Fetch all 5 days in parallel. Scope to the selected profile so heavy
      // accounts only load that profile's slots (the grid never shows others).
      const results = await Promise.all(
        dayRange.map(date =>
          (date === todayStr ? getTodaySchedule(selectedProfileId || undefined) : getScheduleByDate(date, selectedProfileId || undefined))
            .then(data => ({ date, data }))
            .catch(() => ({ date, data: null }))
        )
      )

      const newMultiDay: Record<string, Record<number, ScheduleSlot[]>> = {}
      const newMultiDayStats: Record<string, ScheduleStats> = {}
      let latestProfileStats: ProfileStats[] = []

      let latestRequiresProfile = false
      results.forEach(({ date, data }) => {
        if (data) {
          newMultiDay[date] = data.hourly_slots
          newMultiDayStats[date] = data.stats
          if (date === selectedDate) {
            latestProfileStats = data.profile_stats || []
            latestRequiresProfile = !!data.requires_profile
          }
        } else {
          newMultiDay[date] = {}
          newMultiDayStats[date] = { total: 0, posted: 0, pending: 0, errors: 0 }
        }
      })

      setMultiDaySlots(newMultiDay)
      setMultiDayStats(newMultiDayStats)
      // Use the selected date's data as primary
      setHourlySlots(newMultiDay[selectedDate] || {})
      setStats(newMultiDayStats[selectedDate] || null)
      setProfileStats(latestProfileStats)
      setRequiresProfile(latestRequiresProfile)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load schedule')
    } finally {
      setLoading(false)
    }
  }

  // Fetch request log stats for profiles
  useEffect(() => {
    if (profileStats.length === 0) return
    const fetchRequestLogs = async () => {
      try {
        const adminToken = localStorage.getItem('admin_auth_token')
        if (!adminToken) return
        const res = await axios.get(`${API_URL}/admin/request-logs`, {
          params: { days: 1, sort: 'total_requests', order: 'desc' },
          withCredentials: true,
          headers: { Authorization: `Bearer ${adminToken}` }
        })
        if (res.data?.profiles) {
          const map: Record<string, any> = {}
          for (const p of res.data.profiles) {
            map[p.profile_id] = {
              total_requests: p.total_requests,
              total_errors: p.total_errors,
              total_rate_limited: p.total_rate_limited,
              request_counts: p.request_counts || {}
            }
          }
          setRequestLogStats(map)
        }
      } catch { /* admin-only endpoint, silently fail for non-admins */ }
    }
    fetchRequestLogs()
  }, [profileStats.length])

  const handlePostsPerDaySave = async (profileId: string, value: string) => {
    const num = parseInt(value, 10)
    if (isNaN(num) || num < 1 || num > 1000) {
      setEditingPostsPerDay(null)
      return
    }
    try {
      await updateProfilePostsPerDay(profileId, num)
      setSavedPostsPerDay(profileId)
      setTimeout(() => setSavedPostsPerDay(null), 1500)
      setProfileStats(prev => prev.map(p =>
        p._id === profileId ? {...p, max_daily: num} : p
      ))
    } catch (e) {
      console.error('Failed to update posts_per_day:', e)
    }
    setEditingPostsPerDay(null)
  }

  // Admin: save (or clear, when `clear` is true) the per-profile join-cap override.
  const handleSaveJoinCapOverride = async (clear: boolean) => {
    if (!joinCapModal) return
    const num = clear ? null : parseInt(joinCapValue, 10)
    if (!clear && (isNaN(num as number) || (num as number) < 1 || (num as number) > 100)) {
      setJoinCapError('הזן מספר בין 1 ל-100')
      return
    }
    setJoinCapBusy(true)
    setJoinCapError(null)
    try {
      const res = await updateProfileJoinCapOverride(joinCapModal._id, num)
      if (!res.success) throw new Error(res.error || 'failed')
      setProfileStats(prev => prev.map(p =>
        p._id === joinCapModal._id
          ? {...p, join_cap_override: res.join_cap_override || 0, effective_join_cap: res.effective_join_cap}
          : p
      ))
      setJoinCapModal(null)
    } catch (e: any) {
      setJoinCapError(e?.response?.data?.error || e?.message || 'שמירה נכשלה')
    }
    setJoinCapBusy(false)
  }

  // --- Per-profile group blacklist -------------------------------------------
  // The bot parks a (profile, group) pair here after cannot_post_in_group /
  // group_unavailable, and scheduling then skips that pair. Left alone the list
  // grows until a profile has no postable campaign group left and drops to 0
  // scheduled posts — this popup is how you see that and undo it.
  const openBlacklistModal = async (profile: ProfileStats) => {
    setBlacklistModal(profile)
    setBlacklistGroups([])
    setBlacklistError(null)
    setBlacklistLoading(true)
    try {
      const res = await getProfileBlacklistedGroups(profile._id)
      setBlacklistGroups(res.groups || [])
      setBlacklistCanEdit(!!res.can_edit)
    } catch (e: any) {
      setBlacklistError(e?.response?.data?.error || e?.message || 'טעינת הקבוצות החסומות נכשלה')
    }
    setBlacklistLoading(false)
  }

  const handleUnblacklistGroup = async (groupId: string) => {
    if (!blacklistModal) return
    setBlacklistBusy(groupId)
    setBlacklistError(null)
    try {
      const res = await unblacklistProfileGroup(blacklistModal._id, groupId)
      if (!res.success) throw new Error(res.error || 'failed')
      setBlacklistGroups(prev => prev.filter(g => g._id !== groupId))
      setProfileStats(prev => prev.map(p =>
        p._id === blacklistModal._id ? {...p, blacklisted_count: res.remaining ?? Math.max(0, (p.blacklisted_count || 1) - 1)} : p
      ))
    } catch (e: any) {
      setBlacklistError(e?.response?.data?.error || e?.message || 'ביטול החסימה נכשל')
    }
    setBlacklistBusy(null)
  }

  const handleClearBlacklist = async () => {
    if (!blacklistModal) return
    if (!window.confirm(`לבטל את החסימה של כל ${blacklistGroups.length} הקבוצות עבור ${blacklistModal.name}?`)) return
    setBlacklistBusy('all')
    setBlacklistError(null)
    try {
      const res = await clearProfileBlacklist(blacklistModal._id)
      if (!res.success) throw new Error(res.error || 'failed')
      setBlacklistGroups([])
      setProfileStats(prev => prev.map(p =>
        p._id === blacklistModal._id ? {...p, blacklisted_count: 0} : p
      ))
    } catch (e: any) {
      setBlacklistError(e?.response?.data?.error || e?.message || 'ביטול החסימות נכשל')
    }
    setBlacklistBusy(null)
  }

  const formatHour = (hour: number) => {
    return `${hour.toString().padStart(2, '0')}:00`
  }

  const toggleProfileFilter = (profileId: string) => {
    const params = new URLSearchParams(searchParams)
    if (selectedProfileId === profileId) {
      params.delete('profile')
    } else {
      params.set('profile', profileId)
    }
    setSelectedCampaignId('')
    setSearchParams(params, { replace: true })
  }

  // Admin (logged in as client) profile management. Add creates a blank record
  // only; the client connects it later via the normal QR flow. Delete is soft.
  const [adminProfileBusy, setAdminProfileBusy] = useState(false)
  const handleAdminAddProfile = async () => {
    setAdminProfileBusy(true)
    try {
      const res = await adminAddProfile()
      if (res.success) {
        alert('פרופיל נוצר ✓\nהפרופיל יופיע ברשימה לאחר שהלקוח יחבר אותו (יצירת ברקוד בעת חיבור).')
        loadData()
      } else alert(res.error || 'יצירת הפרופיל נכשלה')
    } catch (e: any) {
      alert('יצירת הפרופיל נכשלה: ' + (e?.response?.data?.error || e?.message))
    } finally {
      setAdminProfileBusy(false)
    }
  }
  const handleAdminDeleteProfile = async (profile: ProfileStats) => {
    if (!window.confirm(`למחוק את הפרופיל "${profile.name}"?\n\n⚠️ מחיקה רכה: הפרופיל יוסתר מהמערכת (ניתן לשחזר), כולל הפרופילים המשניים שלו. הפוסטים והתזמונים שלו יפסיקו לפעול.`)) return
    setAdminProfileBusy(true)
    try {
      const res = await adminSoftDeleteProfile(profile._id)
      if (res.success) {
        if (selectedProfileId === profile._id) {
          const p = new URLSearchParams(searchParams); p.delete('profile'); setSearchParams(p, { replace: true })
        }
        loadData()
      } else alert(res.error || 'המחיקה נכשלה')
    } catch (e: any) {
      alert('המחיקה נכשלה: ' + (e?.response?.data?.error || e?.message))
    } finally {
      setAdminProfileBusy(false)
    }
  }

  // Facebook access token for profile pictures
  const FB_ACCESS_TOKEN = process.env.REACT_APP_FB_ACCESS_TOKEN

  const getProfilePic = (user?: { name?: string; profile_picture_url?: string; facebook_id?: string } | null) => {
    // Use Facebook Graph API with access token if facebook_id is available
    if (user?.facebook_id) {
      return `https://graph.facebook.com/${user.facebook_id}/picture?type=normal&access_token=${FB_ACCESS_TOKEN}`
    }
    // Use actual profile picture if available
    if (user?.profile_picture_url) {
      return user.profile_picture_url
    }
    // Fallback to initials avatar using full name
    const name = user?.name || '?'
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&color=fff&size=40`
  }

  const handleSlotClick = async (slot: ScheduleSlot) => {
    // Update URL without reloading or scrolling
    window.history.replaceState(null, '', `/schedule/${slot._id}`)

    setSelectedSlot(slot)
    setSlotDetails(null)
    setPostNowError(null)
    setPostNowSuccess(null)
    setVerifyPostResult(null)

    setLoadingDetails(true)

    try {
      const response = await getSlotDetails(slot._id)
      setSlotDetails(response.slot)
    } catch (err: any) {
      console.error('Error loading slot details:', err)
    } finally {
      setLoadingDetails(false)
    }
  }

  const handlePostNow = async () => {
    if (!selectedSlot) return

    setPostingNow(true)
    setPostNowError(null)
    setPostNowSuccess(null)
    setPostUrl(null)

    try {
      const response = await postNow(selectedSlot._id)
      if (response.success) {
        setPostNowSuccess('הפוסט נשלח בהצלחה!')
        // Fetch updated slot details to get the facebook_post_id
        try {
          const updatedSlot = await getSlotDetails(selectedSlot._id)
          setSlotDetails(updatedSlot.slot)
          if (updatedSlot.slot.submitted_post?.facebook_post_id && updatedSlot.slot.group?.group_id) {
            setPostUrl(`https://www.facebook.com/groups/${updatedSlot.slot.group.group_id}/posts/${updatedSlot.slot.submitted_post.facebook_post_id}`)
          }
        } catch (e) {
          console.log('Could not fetch updated slot details')
        }
        // Reload data after successful post
        setTimeout(() => {
          loadData()
        }, 3000)
      } else {
        setPostNowError(response.error || 'Failed to post')
      }
    } catch (err: any) {
      setPostNowError(err.response?.data?.error || 'Failed to post')
    } finally {
      setPostingNow(false)
    }
  }

  const handleRetry = async () => {
    if (!selectedSlot) return

    setPostingNow(true)
    setPostNowError(null)
    setPostNowSuccess(null)
    setPostUrl(null)

    try {
      // First reset the error status
      await resetError(selectedSlot._id)

      // Then try posting again
      const response = await postNow(selectedSlot._id)
      if (response.success) {
        setPostNowSuccess('הפוסט נשלח בהצלחה!')
        // Fetch updated slot details to get the facebook_post_id
        try {
          const updatedSlot = await getSlotDetails(selectedSlot._id)
          setSlotDetails(updatedSlot.slot)
          if (updatedSlot.slot.submitted_post?.facebook_post_id && updatedSlot.slot.group?.group_id) {
            setPostUrl(`https://www.facebook.com/groups/${updatedSlot.slot.group.group_id}/posts/${updatedSlot.slot.submitted_post.facebook_post_id}`)
          }
        } catch (e) {
          console.log('Could not fetch updated slot details')
        }
        // Reload data after successful post
        setTimeout(() => {
          loadData()
        }, 3000)
      } else {
        setPostNowError(response.error || 'Failed to post')
      }
    } catch (err: any) {
      setPostNowError(err.response?.data?.error || 'Failed to retry post')
    } finally {
      setPostingNow(false)
    }
  }

  const closeModal = () => {
    // Clear URL without scrolling
    window.history.replaceState(null, '', '/schedule')

    setSelectedSlot(null)
    setSlotDetails(null)
    setPostNowError(null)
    setPostNowSuccess(null)
    setPostUrl(null)
    setVerifyPostResult(null)
  }

  const handleResetJoinGroup = async () => {
    if (!selectedSlot) return

    setResettingJoinGroup(true)
    setPostNowError(null)
    setPostNowSuccess(null)

    try {
      const response = await resetJoinGroup(selectedSlot._id)
      if (response.success) {
        setPostNowSuccess('הפעולה אופסה בהצלחה - תתבצע שוב בשעה הבאה')
        // Reload data after reset
        setTimeout(() => {
          loadData()
          closeModal()
        }, 2000)
      } else {
        setPostNowError(response.error || 'Failed to reset')
      }
    } catch (err: any) {
      setPostNowError(err.response?.data?.error || 'Failed to reset')
    } finally {
      setResettingJoinGroup(false)
    }
  }

  const handleResetAllErrors = async () => {
    if (!window.confirm('לאפס את כל השגיאות של היום?')) return
    setResettingAllErrors(true)
    try {
      const response = await resetAllTodayErrors()
      if (response.success) {
        loadData()
      }
    } catch (err: any) {
      console.error('Failed to reset all errors:', err)
    } finally {
      setResettingAllErrors(false)
    }
  }

  const handleResetAllErrorsSystemWide = async () => {
    if (!window.confirm('⚠️ אדמין: לאפס שגיאות היום בכל החשבונות במערכת? פעולה זו משפיעה על כל המשתמשים.')) return
    setResettingAllErrorsSystemWide(true)
    setResetSystemWideResult(null)
    try {
      const response = await resetAllTodayErrorsSystemWide()
      if (response.success) {
        const b = response.breakdown
        setResetSystemWideResult(
          b
            ? `אופסו ${response.resetCount || 0} (פוסטים: ${b.post_errors}, פנטום: ${b.post_phantoms}, הצטרפויות: ${b.join_group_errors})`
            : `אופסו ${response.resetCount || 0} שגיאות`
        )
        setTimeout(() => setResetSystemWideResult(null), 8000)
        loadData()
      } else {
        setResetSystemWideResult(response.error || 'נכשל')
      }
    } catch (err: any) {
      console.error('Failed to reset all errors system-wide:', err)
      setResetSystemWideResult(err?.response?.data?.error || 'נכשל')
    } finally {
      setResettingAllErrorsSystemWide(false)
    }
  }

  const handleResetGetGroups = async () => {
    setResettingGetGroups(true)
    setResetGetGroupsResult(null)
    try {
      const response = await resetGetGroups()
      if (response.success) {
        setResetGetGroupsResult(`אופסו ${response.resetCount || 0} פרופילים`)
        setTimeout(() => setResetGetGroupsResult(null), 5000)
        loadData()
      }
    } catch (err: any) {
      console.error('Failed to reset get groups:', err)
    } finally {
      setResettingGetGroups(false)
    }
  }

  const handleJoinGroupNow = async () => {
    if (!selectedSlot) return

    setJoiningGroupNow(true)
    setPostNowError(null)
    setPostNowSuccess(null)

    try {
      const response = await joinGroupNow(selectedSlot._id)
      if (response.success) {
        setPostNowSuccess('בקשת ההצטרפות נשלחה בהצלחה!')
        // Reload data after join
        setTimeout(() => {
          loadData()
        }, 3000)
      } else {
        setPostNowError(response.error || 'Failed to join group')
      }
    } catch (err: any) {
      setPostNowError(err.response?.data?.error || 'Failed to join group')
    } finally {
      setJoiningGroupNow(false)
    }
  }

  const handleDeleteAllTodayJoinGroups = async () => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את כל ההצטרפויות לקבוצות של היום?')) {
      return
    }

    setDeletingJoinGroups(true)

    try {
      const response = await deleteAllTodayJoinGroups()
      if (response.success) {
        alert(`נמחקו ${response.deletedCount} הצטרפויות לקבוצות`)
        loadData()
      } else {
        alert(response.error || 'Failed to delete')
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete')
    } finally {
      setDeletingJoinGroups(false)
    }
  }

  const handleVerifyPost = async () => {
    if (!selectedSlot) return

    setVerifyingPost(true)
    setVerifyPostResult(null)

    try {
      const response = await verifyPost(selectedSlot._id)
      if (response.success) {
        setVerifyPostResult({
          success: true,
          message: response.exists
            ? 'הפוסט אומת בהצלחה - קיים בפייסבוק'
            : 'הפוסט לא נמצא - סומן כנמחק'
        })
        // Refresh slot details to get updated verification status
        try {
          const updatedSlot = await getSlotDetails(selectedSlot._id)
          setSlotDetails(updatedSlot.slot)
        } catch (e) {
          console.log('Could not fetch updated slot details')
        }
        // Reload data to update indicators
        setTimeout(() => {
          loadData()
        }, 1000)
      } else {
        setVerifyPostResult({
          success: false,
          message: response.error || 'אימות הפוסט נכשל'
        })
      }
    } catch (err: any) {
      setVerifyPostResult({
        success: false,
        message: err.response?.data?.error || 'אימות הפוסט נכשל'
      })
    } finally {
      setVerifyingPost(false)
    }
  }

  const openJoinForProfile = async () => {
    setShowJoinForProfile(true)
    setJfpProfiles(selectedProfileId ? [selectedProfileId] : [])
    setJfpCampaignIds([])
    // Reuse the reschedule preview just to list the account's campaigns.
    try {
      const resp = await getReschedulePreview()
      setJfpCampaigns((resp.campaigns || []).map((c: any) => ({_id: c._id, name: c.name})))
    } catch { setJfpCampaigns([]) }
  }
  const toggleJfpProfile = (id: string) =>
    setJfpProfiles((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id])
  const toggleJfpCampaign = (id: string) =>
    setJfpCampaignIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id])

  const submitJoinForProfile = async () => {
    if (!jfpProfiles.length || !jfpCampaignIds.length || !jfpCount) return
    setJfpBusy(true)
    try {
      const res = await joinCampaignGroupsForProfilesBulk(jfpProfiles, jfpCampaignIds, jfpCount)
      if (res.success) {
        alert(res.message || `נוצרו ${res.joinGroupsCreated || 0} הצטרפויות`)
        setShowJoinForProfile(false)
        loadData()
      } else alert(res.error || 'נכשל')
    } catch (e: any) {
      alert('נכשל: ' + (e?.response?.data?.error || e?.message))
    } finally {
      setJfpBusy(false)
    }
  }

  const handleOpenRescheduleModal = async () => {
    setShowRescheduleModal(true)
    setLoadingReschedulePreview(true)
    setReschedulePreview(null)
    setRescheduleResult(null)

    try {
      const response = await getReschedulePreview()
      setReschedulePreview(response)
    } catch (err: any) {
      console.error('Error loading reschedule preview:', err)
      setRescheduleResult(err.response?.data?.error || 'Failed to load preview')
    } finally {
      setLoadingReschedulePreview(false)
    }
  }

  // Toggle full-capacity duplicate mode for this account, then refresh the preview
  // so the admin immediately sees the new estimated capacity.
  const handleToggleDuplicateMode = async () => {
    if (!reschedulePreview) return
    const next = !reschedulePreview.allow_duplicate_posts
    if (next && !window.confirm('להפעיל מצב תפוקה מלאה?\nאותו פוסט יוכל להתפרסם שוב באותה קבוצה — אך תמיד על ידי פרופיל אחר.')) return
    setTogglingDuplicateMode(true)
    try {
      const res = await setDuplicatePostsMode(next)
      if (res.success) {
        // Re-run the preview with the new mode
        const response = await getReschedulePreview()
        setReschedulePreview(response)
      } else {
        alert(res.error || 'שגיאה בעדכון המצב')
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'שגיאה בעדכון המצב')
    } finally {
      setTogglingDuplicateMode(false)
    }
  }

  const handleRescheduleAllCampaigns = async () => {
    setRescheduling(true)
    setRescheduleResult(null)

    try {
      const response = await rescheduleAllCampaigns()
      if (response.success) {
        setRescheduleResult(`בוצע בהצלחה! ${response.message}`)
        // Reload data after reschedule
        setTimeout(() => {
          loadData()
        }, 2000)
      } else {
        setRescheduleResult(response.error || 'Failed to reschedule')
      }
    } catch (err: any) {
      setRescheduleResult(err.response?.data?.error || 'Failed to reschedule')
    } finally {
      setRescheduling(false)
    }
  }

  const closeRescheduleModal = () => {
    setShowRescheduleModal(false)
    setReschedulePreview(null)
    setRescheduleResult(null)
  }

  const [clearingDay, setClearingDay] = useState(false)
  const handleClearDay = async () => {
    if (!window.confirm(`למחוק את התזמונים שעדיין לא פורסמו בתאריך ${selectedDate}? פוסטים שכבר פורסמו בפייסבוק לא יימחקו מפייסבוק ויישארו ברשימה (עם הקישור) למחיקה ידנית. פעולה זו אינה הפיכה.`)) {
      return
    }
    setClearingDay(true)
    try {
      const res = await clearScheduleDay(selectedDate)
      alert(`נמחקו ${res.deleted_slots || 0} תזמונים שלא פורסמו.` + ((res as any).kept_published_posts || (res as any).kept_published_slots ? ` ${(res as any).kept_published_posts || (res as any).kept_published_slots} פוסטים שכבר פורסמו נשארו בפייסבוק — יש למחוק אותם ידנית.` : ''))
      await loadData()
    } catch (err: any) {
      alert(err.response?.data?.error || 'שגיאה במחיקת היום')
    } finally {
      setClearingDay(false)
    }
  }

  const isToday = selectedDate === israelToday()

  return (
    <>
      <div className="card">
        <div className="card-header border-0 pt-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold fs-3 mb-1">לוח זמנים</span>
            <span className="text-muted fw-semibold fs-7">
              5 ימים אחרונים - פוסטים מתוזמנים לפי שעות
            </span>
          </h3>
          <div className="card-toolbar gap-3">
            <input
              type="date"
              className="form-control form-control-sm w-auto"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
            <button
              className="btn btn-sm btn-light-primary"
              onClick={loadData}
              disabled={loading}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr029.svg' className='svg-icon-3 me-1' />
              רענן
            </button>
            {/* One place to set the join cap for the whole account. The per-profile ⚡ on
                each row stays for the odd exception, but the cap is normally an
                account-level decision, so it shouldn't need editing page by page. */}
            {canAdminProfiles && (
              <button
                className="btn btn-sm btn-light-warning"
                onClick={() => {
                  setBulkJoinCapOpen(true)
                  setBulkJoinCapValue('')
                  setBulkJoinCapError(null)
                  setBulkJoinCapDone(null)
                }}
                title="אדמין: מכסת הצטרפות לקבוצות ליום — לכל הפרופילים"
              >
                <i className="bi bi-lightning-charge me-1"></i>
                מכסת הצטרפות
              </button>
            )}
            <button
              className="btn btn-sm btn-light-danger"
              onClick={handleClearDay}
              disabled={clearingDay}
              title="מחיקת התזמונים שעדיין לא פורסמו ביום הנבחר (פוסטים שפורסמו לא נמחקים מפייסבוק)"
            >
              {clearingDay ? (
                <span className="spinner-border spinner-border-sm me-1"></span>
              ) : (
                <i className="bi bi-trash me-1"></i>
              )}
              מחק יום זה
            </button>
            {isToday && stats && stats.join_groups && stats.join_groups > 0 && (
              <button
                className="btn btn-sm btn-light-danger"
                onClick={handleDeleteAllTodayJoinGroups}
                disabled={deletingJoinGroups}
              >
                {deletingJoinGroups ? (
                  <span className="spinner-border spinner-border-sm me-1"></span>
                ) : (
                  <i className="bi bi-trash me-1"></i>
                )}
                מחק הצטרפויות היום
              </button>
            )}
            <button
              className="btn btn-sm btn-light-info"
              onClick={openJoinForProfile}
              title="תזמון הצטרפות של פרופיל מסוים למספר קבוצות מקמפיין, מפוזר על היום"
            >
              <i className="bi bi-people-fill me-1"></i>
              הצטרפות לקבוצות לפרופיל
            </button>
            <button
              className="btn btn-sm btn-light-success"
              onClick={handleOpenRescheduleModal}
            >
              <i className="bi bi-calendar-check me-1"></i>
              תזמון מחדש לכל הקמפיינים
            </button>
          </div>
        </div>

        <div className="card-body py-3">
          {/* Stats */}
          {stats && (
            <div className="d-flex gap-4 mb-5 flex-wrap">
              <div className="border border-gray-300 border-dashed rounded py-3 px-4">
                <div className="fs-4 fw-bold text-gray-700">{stats.total}</div>
                <div className="fw-semibold text-muted">סה״כ</div>
              </div>
              <div className="border border-success border-dashed rounded py-3 px-4">
                <div className="fs-4 fw-bold text-success">{stats.posted}</div>
                <div className="fw-semibold text-muted">פורסמו</div>
              </div>
              <div className="border border-warning border-dashed rounded py-3 px-4">
                <div className="fs-4 fw-bold text-warning">{stats.pending}</div>
                <div className="fw-semibold text-muted">ממתינים</div>
              </div>
              {(() => {
                const missingPostIdCount = Object.values(hourlySlots).flat().filter(s => s.has_missing_post_id).length
                const totalFails = stats.errors + missingPostIdCount
                return totalFails > 0 ? (
                  <div className="border border-danger border-dashed rounded py-3 px-4 d-flex align-items-center gap-3">
                    <div>
                      <div className="fs-4 fw-bold text-danger">{totalFails}</div>
                      <div className="fw-semibold text-muted">שגיאות</div>
                    </div>
                    <button
                      className="btn btn-sm btn-light-danger"
                      onClick={handleResetAllErrors}
                      disabled={resettingAllErrors}
                    >
                      {resettingAllErrors ? (
                        <span className="spinner-border spinner-border-sm" />
                      ) : (
                        <>
                          <i className="bi bi-arrow-counterclockwise me-1"></i>
                          אפס הכל
                        </>
                      )}
                    </button>
                  </div>
                ) : null
              })()}
              {isAdminMode && (
                <div className="border border-dashed rounded py-3 px-4 d-flex align-items-center gap-3" style={{borderColor: '#7239ea', background: '#f8f5ff'}}>
                  <div>
                    <div className="fs-7 fw-bold" style={{color: '#7239ea'}}>אדמין</div>
                    <div className="fw-semibold text-muted" style={{fontSize: '11px'}}>אפס שגיאות בכל המערכת</div>
                    {resetSystemWideResult && (
                      <div style={{fontSize: '10px', color: '#7239ea', marginTop: '2px'}}>{resetSystemWideResult}</div>
                    )}
                  </div>
                  <button
                    className="btn btn-sm"
                    style={{background: '#7239ea', color: '#fff'}}
                    onClick={handleResetAllErrorsSystemWide}
                    disabled={resettingAllErrorsSystemWide}
                    title="מאפס שגיאות היום בכל החשבונות במערכת"
                  >
                    {resettingAllErrorsSystemWide ? (
                      <span className="spinner-border spinner-border-sm" />
                    ) : (
                      <>
                        <i className="bi bi-arrow-counterclockwise me-1"></i>
                        אפס הכל (מערכת)
                      </>
                    )}
                  </button>
                </div>
              )}
              {isToday && (() => {
                const missedCount = Object.entries(hourlySlots).reduce((count, [hour, slots]) => {
                  if (parseInt(hour) < currentHour) {
                    return count + slots.filter(s => !s.posted && !s.is_error && s.slot_type !== 'join_group').length
                  }
                  return count
                }, 0)
                return missedCount > 0 ? (
                  <div className="border border-dashed rounded py-3 px-4" style={{borderColor: '#ff6b35'}}>
                    <div className="fs-4 fw-bold" style={{color: '#ff6b35'}}>{missedCount}</div>
                    <div className="fw-semibold text-muted">פספוסים</div>
                  </div>
                ) : null
              })()}
              {stats.join_groups && stats.join_groups > 0 && (
                <div className="border border-info border-dashed rounded py-3 px-4">
                  <div className="fs-4 fw-bold text-info">{stats.join_groups}</div>
                  <div className="fw-semibold text-muted">הצטרפות לקבוצות</div>
                </div>
              )}
              {(() => {
                const lowGroupCount = profileStats.filter(p => p.groups_following < 10).length
                return lowGroupCount > 0 ? (
                  <div className="border border-primary border-dashed rounded py-3 px-4 d-flex align-items-center gap-3">
                    <div>
                      <div className="fs-4 fw-bold text-primary">{lowGroupCount}</div>
                      <div className="fw-semibold text-muted">פחות מ-10 קבוצות</div>
                    </div>
                    <button
                      className="btn btn-sm btn-light-primary"
                      onClick={handleResetGetGroups}
                      disabled={resettingGetGroups}
                    >
                      {resettingGetGroups ? (
                        <span className="spinner-border spinner-border-sm" />
                      ) : (
                        <>
                          <i className="bi bi-arrow-counterclockwise me-1"></i>
                          אפס get groups
                        </>
                      )}
                    </button>
                    {resetGetGroupsResult && (
                      <span className="text-success fs-7">{resetGetGroupsResult}</span>
                    )}
                  </div>
                ) : null
              })()}
            </div>
          )}

          {/* Admin: add the first profile when the account has none yet */}
          {canAdminProfiles && !loading && profileStats.length === 0 && (
            <div className="mb-5">
              <button className="btn btn-sm btn-light-primary" onClick={handleAdminAddProfile} disabled={adminProfileBusy}>
                {adminProfileBusy ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-plus-lg me-1"></i>}
                הוסף פרופיל (אדמין)
              </button>
              <span className="text-muted ms-2" style={{fontSize: 12}}>אין פרופילים מחוברים — הוספה תיצור רשומת פרופיל לחיבור בהמשך.</span>
            </div>
          )}

          {/* Profile Stats */}
          {profileStats.length > 0 && (
            <div className="mb-5">
              <div className="d-flex align-items-center gap-3 mb-3">
                <h6 className="fw-bold text-gray-700 mb-0">סטטיסטיקות לפי פרופיל</h6>
                {selectedProfileId && (
                  <button
                    className="btn btn-sm btn-light-danger py-1 px-3"
                    onClick={() => { const p = new URLSearchParams(searchParams); p.delete('profile'); setSearchParams(p, { replace: true }) }}
                  >
                    <i className="bi bi-x-lg me-1 fs-9"></i>
                    נקה סינון
                  </button>
                )}
                {canAdminProfiles && (
                  <button
                    className="btn btn-sm btn-light-primary py-1 px-3"
                    onClick={handleAdminAddProfile}
                    disabled={adminProfileBusy}
                    title="אדמין: יצירת רשומת פרופיל חדשה (ללא ברקוד — הלקוח יחבר בהמשך)"
                  >
                    {adminProfileBusy ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-plus-lg me-1 fs-9"></i>}
                    הוסף פרופיל (אדמין)
                  </button>
                )}
              </div>
              <div className="d-flex flex-wrap gap-3">
                {profileStats.map(profile => (
                  <div
                    key={profile._id}
                    className={`border rounded p-3 ${selectedProfileId === profile._id ? 'border-primary border-2 shadow-sm' : 'border-gray-300'}`}
                    style={{ minWidth: '200px', cursor: 'pointer', transition: 'all 0.2s ease' }}
                    onClick={() => toggleProfileFilter(profile._id)}
                  >
                    <div className="d-flex align-items-center mb-2">
                      <div className="position-relative me-2">
                        <img
                          src={profile.facebook_id
                            ? `https://graph.facebook.com/${profile.facebook_id}/picture?type=normal&access_token=${process.env.REACT_APP_FB_ACCESS_TOKEN}`
                            : profile.profile_picture_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name)}&background=random&color=fff&size=32`}
                          alt={profile.name}
                          className="rounded-circle"
                          style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name)}&background=random&color=fff&size=32`
                          }}
                        />
                        <span
                          className={`position-absolute rounded-circle border border-2 border-white ${profile.is_online ? 'bg-success' : 'bg-danger'}`}
                          style={{ width: '10px', height: '10px', bottom: '0', right: '0' }}
                        />
                      </div>
                      <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-1">
                          <span className="fw-bold text-gray-800 text-truncate" style={{ maxWidth: '130px' }}>
                            {profile.name}
                          </span>
                          <span className={`badge badge-sm ${profile.is_parent ? 'badge-light-primary' : 'badge-light-info'}`} style={{ fontSize: '9px', padding: '2px 4px' }}>
                            {profile.is_parent ? 'ראשי' : 'משני'}
                          </span>
                          <button
                            className="btn btn-icon btn-sm btn-light-secondary"
                            style={{ width: '18px', height: '18px', minWidth: '18px', padding: 0 }}
                            title={profile._id}
                            onClick={(e) => {
                              e.stopPropagation()
                              navigator.clipboard.writeText(profile._id)
                              const btn = e.currentTarget
                              btn.innerHTML = '<i class="bi bi-check fs-9 text-success"></i>'
                              setTimeout(() => { btn.innerHTML = '<i class="bi bi-clipboard fs-9"></i>' }, 1500)
                            }}
                          >
                            <i className="bi bi-clipboard fs-9"></i>
                          </button>
                          <a
                            href={`http://144.91.72.128:8080/preview/${profile._id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-icon btn-sm btn-light-primary"
                            style={{ width: '18px', height: '18px', minWidth: '18px', padding: 0 }}
                            title="התחבר לפרופיל"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="bi bi-box-arrow-up-right fs-9"></i>
                          </a>
                          {canAdminProfiles && (
                            <button
                              className={`btn btn-icon btn-sm ${profile.join_cap_override ? 'btn-light-warning' : 'btn-light-info'}`}
                              style={{ width: '18px', height: '18px', minWidth: '18px', padding: 0 }}
                              title={profile.join_cap_override
                                ? `אדמין: מכסת הצטרפות ידנית — ${profile.join_cap_override} קבוצות ליום`
                                : 'אדמין: מכסת הצטרפות לקבוצות ליום'}
                              onClick={(e) => {
                                e.stopPropagation()
                                setJoinCapModal(profile)
                                setJoinCapValue(profile.join_cap_override ? String(profile.join_cap_override) : '')
                                setJoinCapError(null)
                              }}
                            >
                              <i className="bi bi-lightning-charge fs-9"></i>
                            </button>
                          )}
                          {canAdminProfiles && (
                            <button
                              className="btn btn-icon btn-sm btn-light-danger"
                              style={{ width: '18px', height: '18px', minWidth: '18px', padding: 0 }}
                              title="אדמין: מחיקה רכה של הפרופיל"
                              disabled={adminProfileBusy}
                              onClick={(e) => { e.stopPropagation(); handleAdminDeleteProfile(profile) }}
                            >
                              <i className="bi bi-trash fs-9"></i>
                            </button>
                          )}
                        </div>
                        {!profile.is_parent && profile.parent_name && (
                          <span className="text-muted fs-9 text-truncate" style={{ maxWidth: '150px' }}>
                            ← {profile.parent_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted fs-7">פוסטים היום:</span>
                      <span className="fw-bold">
                        {profile.slots_total} / {editingPostsPerDay?.profileId === profile._id ? (
                          <input
                            type="number"
                            autoFocus
                            value={editingPostsPerDay.value}
                            onChange={(e) => setEditingPostsPerDay({profileId: profile._id, value: e.target.value})}
                            onBlur={() => handlePostsPerDaySave(profile._id, editingPostsPerDay.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handlePostsPerDaySave(profile._id, editingPostsPerDay!.value)
                              if (e.key === 'Escape') setEditingPostsPerDay(null)
                            }}
                            min={1}
                            max={1000}
                            style={{width: '55px', padding: '0 4px', border: '1px solid #3699ff', borderRadius: '4px', fontSize: '13px', fontWeight: 600, textAlign: 'center'}}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <span
                            onClick={(e) => {
                              e.stopPropagation()
                              setEditingPostsPerDay({profileId: profile._id, value: String(profile.max_daily)})
                            }}
                            style={{
                              cursor: 'pointer',
                              borderBottom: '1px dashed #b5b5c3',
                              color: savedPostsPerDay === profile._id ? '#1bc5bd' : undefined,
                              transition: 'color 0.3s ease',
                            }}
                            title="לחץ לעריכה"
                          >
                            {profile.max_daily}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="progress h-6px mb-2" style={{ backgroundColor: '#e4e6ef' }}>
                      <div
                        className={`progress-bar ${profile.usage_percent >= 90 ? 'bg-danger' : profile.usage_percent >= 70 ? 'bg-warning' : 'bg-success'}`}
                        style={{ width: `${Math.min(profile.usage_percent, 100)}%` }}
                      />
                    </div>
                    <div className="d-flex gap-2 fs-8">
                      <span className="text-success">{profile.slots_posted} פורסמו</span>
                      <span className="text-warning">{profile.slots_pending} ממתינים</span>
                      {profile.slots_errors > 0 && (
                        <span className="text-danger">{profile.slots_errors} שגיאות</span>
                      )}
                    </div>
                    {/* Cross-post eligibility: how many of today's slots target a group
                        the page can actually xpost into (per FB's crosspost picker).
                        Ineligible slots are aborted by the bot, not single-posted. */}
                    {(() => {
                      const elig = profile.xpost_eligible_slots ?? 0
                      const total = profile.slots_total || 0
                      const cache = profile.xpost_cache_size ?? 0
                      const schedGroups = profile.xpost_sched_groups ?? 0
                      const eligGroups = profile.xpost_eligible_sched_groups ?? 0
                      const checked = profile.xpost_cache_checked_at
                      const ageH = checked ? Math.round((Date.now() - new Date(checked).getTime()) / 3600000) : null
                      const cls = elig === 0 ? 'text-danger' : elig < total ? 'text-warning' : 'text-success'
                      const gcls = eligGroups === 0 ? 'text-danger' : eligGroups < schedGroups ? 'text-warning' : 'text-success'
                      return (
                        <>
                          <div
                            className="fs-8 mt-1 d-flex justify-content-between"
                            title={`הפרופיל מזוהה כמאופשר קרוס-פוסט ב-${cache} קבוצות (לפי בורר הקבוצות של פייסבוק${checked ? `, נבדק לפני ${ageH} ש׳` : ', טרם נבדק'}). ${elig}/${total} מהפוסטים היום מתוזמנים לקבוצה כזו — פוסטים בקבוצה שאינה ברשימה מבוטלים ולא מתפרסמים.`}
                          >
                            <span className="text-muted">קרוס-פוסט (פוסטים):</span>
                            <span className={`fw-bold ${cls}`}>
                              {elig}/{total} <span className="text-muted">({cache} בזיכרון{ageH !== null ? ` · ${ageH}ש׳` : ''})</span>
                            </span>
                          </div>
                          <div
                            className="fs-8 d-flex justify-content-between"
                            title={`מתוך ${schedGroups} הקבוצות השונות שמתוזמנות היום, ${eligGroups} נמצאות ברשימת הקרוס-פוסט של הפרופיל. ${schedGroups - eligGroups} קבוצות אינן זמינות לקרוס-פוסט — הפוסטים אליהן מבוטלים.`}
                          >
                            <span className="text-muted">קבוצות מתוזמנות זמינות:</span>
                            <span className={`fw-bold ${gcls}`}>{eligGroups}/{schedGroups}</span>
                          </div>
                        </>
                      )
                    })()}
                    {profile.join_groups > 0 && (
                      <div className="fs-8 text-info mt-1">{profile.join_groups} הצטרפויות לקבוצות</div>
                    )}
                    {/* Last activity timestamps */}
                    <div className="mt-2 pt-2 border-top fs-8">
                      <div className="d-flex justify-content-between text-muted">
                        <span>קבוצות ({profile.groups_following}):</span>
                        <span className={profile.last_get_groups ? '' : 'text-danger'}>
                          {profile.last_get_groups
                            ? new Date(profile.last_get_groups).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                            : 'אף פעם'}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between text-muted">
                        <span>התראות:</span>
                        <span className={profile.last_get_notifications ? '' : 'text-danger'}>
                          {profile.last_get_notifications
                            ? new Date(profile.last_get_notifications).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                            : 'אף פעם'}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between text-muted">
                        <span>מרקטפלייס:</span>
                        <span className={profile.last_get_marketplace ? '' : 'text-danger'}>
                          {profile.last_get_marketplace
                            ? new Date(profile.last_get_marketplace).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                            : 'אף פעם'}
                        </span>
                      </div>
                      {/* Groups the bot parked as unusable for this profile. Scheduling
                          skips them, so a growing list here is the usual reason a
                          profile's volume drops without any setting changing. */}
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">קבוצות חסומות:</span>
                        <button
                          className={`btn btn-sm p-0 fs-8 fw-bold ${(profile.blacklisted_count || 0) > 0 ? 'text-danger' : 'text-muted'}`}
                          style={{ background: 'none', border: 'none', textDecoration: (profile.blacklisted_count || 0) > 0 ? 'underline' : 'none' }}
                          title="קבוצות שהבוט סימן כלא-זמינות לפרופיל הזה (אין הרשאת פרסום / הקבוצה לא נטענת). התזמון מדלג עליהן — לחץ לצפייה וביטול חסימה."
                          onClick={(e) => { e.stopPropagation(); openBlacklistModal(profile) }}
                        >
                          {profile.blacklisted_count || 0}
                          {(profile.blacklisted_count || 0) > 0 && <i className="bi bi-slash-circle ms-1 fs-9"></i>}
                        </button>
                      </div>
                    </div>
                    {/* Request Log Stats */}
                    {profile.facebook_id && requestLogStats[profile.facebook_id] && (() => {
                      const rl = requestLogStats[profile.facebook_id]
                      const topRequests = Object.entries(rl.request_counts).sort(([,a],[,b]) => b - a).slice(0, 3)
                      return (
                        <div className="mt-2 pt-2 border-top">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="text-muted fs-8">בקשות היום:</span>
                            <span className={`fw-bold fs-8 ${rl.total_requests > 1000 ? 'text-danger' : rl.total_requests > 500 ? 'text-warning' : ''}`}>
                              {rl.total_requests.toLocaleString()}
                            </span>
                          </div>
                          {rl.total_errors > 0 && (
                            <div className="d-flex justify-content-between fs-8">
                              <span className="text-danger">שגיאות:</span>
                              <span className="text-danger fw-bold">{rl.total_errors.toLocaleString()}</span>
                            </div>
                          )}
                          {rl.total_rate_limited > 0 && (
                            <div className="d-flex justify-content-between fs-8">
                              <span className="text-warning">Rate Limited:</span>
                              <span className="text-warning fw-bold">{rl.total_rate_limited.toLocaleString()}</span>
                            </div>
                          )}
                          {topRequests.length > 0 && (
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {topRequests.map(([name, count]) => (
                                <span key={name} className="fs-9" style={{
                                  padding: '1px 4px', borderRadius: '3px', fontSize: '9px',
                                  background: name.includes('Mutation') ? '#fff5f8' : '#f5f8fa',
                                  color: name.includes('Mutation') ? '#f64e60' : '#7e8299'
                                }}>
                                  {count} {name.replace(/^Comet|Query$|Mutation$/g, '').substring(0, 20)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Campaign Breakdown */}
          {!loading && selectedProfileId && (() => {
            // Gather all slots for selected profile
            const allProfileSlots: ScheduleSlot[] = []
            for (let h = 0; h < 24; h++) {
              const hSlots = hourlySlots[h] || []
              hSlots.forEach(s => {
                if (s.user?._id === selectedProfileId && s.slot_type !== 'join_group') allProfileSlots.push(s)
              })
            }
            // Build campaign stats
            const cStatsMap = new Map<string, { name: string; total: number; posted: number; pending: number; errors: number; warmup: number }>()
            allProfileSlots.forEach(s => {
              const cId = s.campaign?._id || 'unknown'
              const cName = s.campaign?.name || 'לא משויך'
              if (!cStatsMap.has(cId)) cStatsMap.set(cId, { name: cName, total: 0, posted: 0, pending: 0, errors: 0, warmup: 0 })
              const c = cStatsMap.get(cId)!
              c.total++
              if (isWarmupSkip(s)) c.warmup++
              else if (s.is_error) c.errors++
              else if (s.posted) c.posted++
              else c.pending++
            })
            const campaigns = Array.from(cStatsMap.entries())

            if (campaigns.length === 0) return null
            return (
              <div className="mb-5">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <h6 className="fw-bold text-gray-700 mb-0">פירוט לפי קמפיין</h6>
                  {selectedCampaignId && (
                    <button
                      className="btn btn-sm btn-light-danger py-1 px-3"
                      onClick={() => setSelectedCampaignId('')}
                    >
                      <i className="bi bi-x-lg me-1 fs-9"></i>
                      הצג הכל
                    </button>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-3">
                  {campaigns.sort((a, b) => b[1].total - a[1].total).map(([cId, c]) => {
                    const color = campaignColorMap.get(cId)!
                    const isSelected = selectedCampaignId === cId
                    return (
                      <div
                        key={cId}
                        className="border rounded p-3"
                        style={{
                          minWidth: '180px',
                          cursor: 'pointer',
                          borderColor: isSelected ? color : '#e4e6ef',
                          borderWidth: isSelected ? '2px' : '1px',
                          borderStyle: 'solid',
                          borderRight: `4px solid ${color}`,
                          backgroundColor: isSelected ? `${color}10` : 'white',
                          transition: 'all 0.2s ease',
                        }}
                        onClick={() => setSelectedCampaignId(isSelected ? '' : cId)}
                      >
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                          <span className="fw-bold text-gray-800 text-truncate" style={{ maxWidth: '140px' }}>{c.name}</span>
                        </div>
                        <div className="d-flex align-items-center gap-1 mb-1">
                          <span className="fs-2 fw-bolder" style={{ color }}>{c.total}</span>
                          <span className="text-muted fs-8">פוסטים</span>
                        </div>
                        <div className="d-flex gap-2 fs-8">
                          <span className="text-success">{c.posted} פורסמו</span>
                          <span className="text-warning">{c.pending} ממתינים</span>
                          {c.errors > 0 && <span className="text-danger">{c.errors} שגיאות</span>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })()}

          {error && (
            <div className="alert alert-danger d-flex align-items-center mb-4">
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2 text-danger me-3' />
              {error}
            </div>
          )}

          {!loading && requiresProfile && !selectedProfileId && (
            <div className="alert alert-primary d-flex align-items-center mb-4">
              <i className="bi bi-info-circle fs-3 me-3"></i>
              <div>
                <div className="fw-bold">חשבון גדול — בחר פרופיל כדי לטעון את הלוח</div>
                <div className="fs-7 text-muted">בגלל כמות הפוסטים/פרופילים, הלוח נטען לפי פרופיל. לחץ על פרופיל למעלה כדי לראות את התזמון שלו.</div>
              </div>
            </div>
          )}
          {loading ? (
            <div className="text-center py-10">
              <span className="spinner-border spinner-border-lg text-primary"></span>
              <p className="text-muted mt-3">טוען לוח זמנים...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-row-bordered table-row-gray-200 align-middle gs-0 gy-1" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr className="fw-bold text-muted bg-light">
                    <th className="ps-4 rounded-start" style={{ width: '60px' }}>שעה</th>
                    {dayRange.map(date => {
                      const d = new Date(date + 'T12:00:00')
                      const dayName = d.toLocaleDateString('he-IL', { weekday: 'short' })
                      const dayNum = d.toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric' })
                      const isSelectedDay = date === selectedDate
                      const isTodayCol = date === israelToday()
                      const dayStats = multiDayStats[date]
                      return (
                        <th key={date} className={`text-center ${isSelectedDay ? 'bg-light-primary' : ''}`}>
                          <div className="d-flex flex-column align-items-center">
                            <span className={`fs-8 ${isTodayCol ? 'text-primary fw-bolder' : ''}`}>{dayName}</span>
                            <span className={`fs-7 ${isSelectedDay ? 'fw-bolder text-primary' : ''}`}>{dayNum}</span>
                            {dayStats && (
                              <div className="d-flex gap-1 mt-1">
                                <span className="badge badge-light-success py-0 px-1 fs-9">{dayStats.posted}</span>
                                <span className="badge badge-light-warning py-0 px-1 fs-9">{dayStats.pending}</span>
                                {dayStats.errors > 0 && <span className="badge badge-light-danger py-0 px-1 fs-9">{dayStats.errors}</span>}
                              </div>
                            )}
                          </div>
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 24 }, (_, hour) => {
                    const todayStr = israelToday()
                    const isPastHour = isToday && hour < currentHour
                    const isCurrentHour = isToday && hour === currentHour

                    return (
                      <tr
                        key={hour}
                        className={isCurrentHour ? 'bg-light-primary' : ''}
                      >
                        <td className="ps-4" style={{ verticalAlign: 'top', paddingTop: '8px' }}>
                          <span className={`fw-bold fs-8 ${isCurrentHour ? 'text-primary' : isPastHour ? 'text-muted' : 'text-dark'}`}>
                            {formatHour(hour)}
                          </span>
                          {isCurrentHour && (
                            <div><span className="badge badge-primary fs-9 px-1 py-0">עכשיו</span></div>
                          )}
                        </td>
                        {dayRange.map(date => {
                          const daySlotsAll = (multiDaySlots[date] || {})[hour] || []
                          let daySlots = selectedProfileId ? daySlotsAll.filter(s => s.user?._id === selectedProfileId) : daySlotsAll
                          if (selectedCampaignId) daySlots = daySlots.filter(s => (s.campaign?._id || 'unknown') === selectedCampaignId)
                          const isSelectedDay = date === selectedDate
                          const isDayToday = date === todayStr
                          const isDayPastHour = isDayToday && hour < currentHour
                          const isDayCurrentHour = isDayToday && hour === currentHour

                          return (
                            <td
                              key={date}
                              className={`${isSelectedDay ? 'bg-light-primary' : ''}`}
                              style={{
                                verticalAlign: 'top',
                                paddingTop: '6px',
                                opacity: isDayPastHour && !isDayCurrentHour ? 0.7 : 1,
                              }}
                            >
                              {daySlots.length === 0 ? (
                                <span className="text-muted fs-8">-</span>
                              ) : (() => {
                                // Cap join-group slots per cell — accounts with thousands of
                                // join_groups (e.g. promise.jerusalem) were rendering 1000+
                                // DOM nodes in a single cell and hanging Chrome.
                                const cellKey = `${date}|${hour}`
                                const isExpanded = expandedJoinCells.has(cellKey)
                                const postSlots = daySlots.filter(s => s.slot_type !== 'join_group')
                                const joinSlots = daySlots.filter(s => s.slot_type === 'join_group')
                                const joinSlotsToRender = isExpanded ? joinSlots : joinSlots.slice(0, JOIN_GROUP_CAP)
                                const joinExtra = joinSlots.length - joinSlotsToRender.length
                                const renderedSlots = [...postSlots, ...joinSlotsToRender]
                                return (
                                <div className="d-flex flex-wrap gap-1">
                                  {renderedSlots.map((slot) => (
                                    <div
                                      key={slot._id}
                                      className="position-relative"
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => handleSlotClick(slot)}
                                    >
                                      <div className="slot-item">
                                        <div
                                          className={`symbol symbol-circle ${
                                            isHardBlocked(slot) ? 'border border-3 border-danger zipper-flash-ring' :
                                            isLimitHit(slot) ? 'border border-3 border-danger' :
                                            isWarmupSkip(slot) ? 'border border-2 border-warning' :
                                            slot.is_error ? 'border border-2 border-danger' :
                                            slot.has_missing_post_id ? 'border border-2 border-danger' :
                                            slot.slot_type === 'join_group' ? 'border border-2 border-info' :
                                            slot.posted ? 'border border-2 border-success' : ''
                                          }`}
                                          style={{
                                            width: '28px',
                                            height: '28px',
                                            minWidth: '28px',
                                            opacity: slot.posted ? 1 : 0.6,
                                            ...(selectedProfileId && campaignColorMap.size > 1 && slot.campaign?._id ? {
                                              boxShadow: `0 0 0 2px ${campaignColorMap.get(slot.campaign._id) || '#ccc'}`,
                                            } : {})
                                          }}
                                        >
                                          <img
                                            src={getProfilePic(slot.user)}
                                            alt={slot.user?.name || 'Profile'}
                                            className="rounded-circle"
                                            style={{ width: '28px', height: '28px', objectFit: 'cover' }}
                                            onError={(e) => {
                                              const name = slot.user?.name || '?'
                                              ;(e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&color=fff&size=28`
                                            }}
                                          />

                                          {/* Join group icon overlay */}
                                          {slot.slot_type === 'join_group' && (
                                            <div
                                              className="position-absolute d-flex align-items-center justify-content-center bg-info text-white"
                                              style={{
                                                width: '12px',
                                                height: '12px',
                                                bottom: '-1px',
                                                right: '-1px',
                                                borderRadius: '50%',
                                                border: '1.5px solid white',
                                                fontSize: '7px'
                                              }}
                                            >
                                              <i className="bi bi-person-plus-fill"></i>
                                            </div>
                                          )}

                                          {/* Status indicator - red dot for missing post ID */}
                                          {slot.slot_type !== 'join_group' && slot.has_missing_post_id && (
                                            <div
                                              className="position-absolute rounded-circle bg-danger"
                                              style={{ width: '10px', height: '10px', bottom: '-1px', right: '-1px', border: '1.5px solid white' }}
                                            />
                                          )}
                                          {/* Status indicator - pending submission (submitted but not yet published) */}
                                          {slot.slot_type !== 'join_group' && slot.posted && !slot.has_missing_post_id && !slot.is_error && slot.submitted_post?.status === 'pending' && (
                                            <div
                                              className="position-absolute rounded-circle bg-warning d-flex align-items-center justify-content-center"
                                              style={{ width: '11px', height: '11px', bottom: '-1px', right: '-1px', border: '1.5px solid white', fontSize: '7px', color: 'white' }}
                                            >
                                              <i className="bi bi-hourglass-split"></i>
                                            </div>
                                          )}
                                          {/* Status indicator - verified */}
                                          {slot.slot_type !== 'join_group' && slot.posted && !slot.has_missing_post_id && !slot.is_error && slot.submitted_post?.status !== 'pending' && slot.submitted_post?.verification_status === 'verified' && (
                                            <div
                                              className="position-absolute rounded-circle bg-success d-flex align-items-center justify-content-center"
                                              style={{ width: '11px', height: '11px', bottom: '-1px', right: '-1px', border: '1.5px solid white', fontSize: '7px', color: 'white' }}
                                            >
                                              <i className="bi bi-check"></i>
                                            </div>
                                          )}
                                          {/* Status indicator - deleted */}
                                          {slot.slot_type !== 'join_group' && slot.posted && !slot.has_missing_post_id && !slot.is_error && slot.submitted_post?.status !== 'pending' && slot.submitted_post?.verification_status === 'deleted' && (
                                            <div
                                              className="position-absolute rounded-circle bg-danger d-flex align-items-center justify-content-center"
                                              style={{ width: '11px', height: '11px', bottom: '-1px', right: '-1px', border: '1.5px solid white', fontSize: '7px', color: 'white' }}
                                            >
                                              <i className="bi bi-x"></i>
                                            </div>
                                          )}
                                          {/* Status indicator - posted (pending verification) */}
                                          {slot.slot_type !== 'join_group' && slot.posted && !slot.has_missing_post_id && !slot.is_error && slot.submitted_post?.status !== 'pending' && (!slot.submitted_post?.verification_status || slot.submitted_post?.verification_status === 'pending' || slot.submitted_post?.verification_status === 'error') && (
                                            <div
                                              className="position-absolute rounded-circle bg-success"
                                              style={{ width: '10px', height: '10px', bottom: '-1px', right: '-1px', border: '1.5px solid white' }}
                                            />
                                          )}
                                          {/* Status indicator - warm-up skipped (intentional throttle, not an error) */}
                                          {slot.slot_type !== 'join_group' && isWarmupSkip(slot) && (
                                            <div
                                              className="position-absolute rounded-circle bg-warning d-flex align-items-center justify-content-center"
                                              style={{ width: '11px', height: '11px', bottom: '-1px', right: '-1px', border: '1.5px solid white', fontSize: '7px', color: 'white' }}
                                              title="דילוג חימום"
                                            >
                                              <i className="bi bi-moon-stars-fill"></i>
                                            </div>
                                          )}
                                          {/* Status indicator - FB hard block (1390008) — flashing red, impossible to miss */}
                                          {slot.slot_type !== 'join_group' && isHardBlocked(slot) && (
                                            <div
                                              className="position-absolute rounded-circle zipper-blink-bg d-flex align-items-center justify-content-center"
                                              style={{ width: '13px', height: '13px', bottom: '-1px', right: '-1px', border: '1.5px solid white', fontSize: '8px', color: 'white' }}
                                              title="נחסם ע״י פייסבוק (1390008)"
                                            >
                                              <i className="bi bi-slash-circle-fill"></i>
                                            </div>
                                          )}
                                          {/* Status indicator - limit triggered (this post got FB's rate-limit response) */}
                                          {slot.slot_type !== 'join_group' && !isHardBlocked(slot) && isLimitHit(slot) && (
                                            <div
                                              className="position-absolute rounded-circle bg-danger d-flex align-items-center justify-content-center"
                                              style={{ width: '12px', height: '12px', bottom: '-1px', right: '-1px', border: '1.5px solid white', fontSize: '8px', color: 'white' }}
                                              title="הפוסט שהגביל את הפרופיל"
                                            >
                                              <i className="bi bi-hand-thumbs-down-fill"></i>
                                            </div>
                                          )}
                                          {/* Status indicator - error */}
                                          {slot.slot_type !== 'join_group' && slot.is_error && !isWarmupSkip(slot) && !isHardBlocked(slot) && !isLimitHit(slot) && (
                                            <div
                                              className="position-absolute rounded-circle bg-danger"
                                              style={{ width: '10px', height: '10px', bottom: '-1px', right: '-1px', border: '1.5px solid white' }}
                                            />
                                          )}
                                        </div>

                                        {/* Hover Tooltip */}
                                        <div className="slot-tooltip">
                                          <div className="fw-bold text-dark">{slot.user?.name || 'Unknown'}</div>
                                          <div className="text-muted fs-8">
                                            <i className="bi bi-people-fill me-1"></i>
                                            {slot.group?.name || 'Unknown Group'}
                                          </div>
                                          {slot.campaign?.name && (
                                            <div className="fs-8" style={{ color: campaignColorMap.get(slot.campaign._id) || '#6366f1' }}>
                                              <i className="bi bi-megaphone-fill me-1"></i>
                                              {slot.campaign.name}
                                            </div>
                                          )}
                                          <div className="text-muted fs-8 mt-1">
                                            {slot.slot_type === 'join_group' ? (
                                              slot.posted ? (
                                                <span className="text-info"><i className="bi bi-check-circle me-1"></i>הצטרף</span>
                                              ) : (
                                                <span className="text-info"><i className="bi bi-person-plus me-1"></i>הצטרפות לקבוצה</span>
                                              )
                                            ) : isWarmupSkip(slot) ? (
                                              <span className="text-warning"><i className="bi bi-moon-stars me-1"></i>דילוג חימום</span>
                                            ) : isHardBlocked(slot) ? (
                                              <span className="text-danger fw-bold"><i className="bi bi-slash-circle-fill me-1"></i>נחסם ע״י פייסבוק (1390008)</span>
                                            ) : isLimitHit(slot) ? (
                                              <span className="text-danger fw-bold"><i className="bi bi-hand-thumbs-down-fill me-1"></i>הפוסט שהגביל את הפרופיל</span>
                                            ) : slot.has_missing_post_id ? (
                                              <span className="text-danger"><i className="bi bi-exclamation-circle me-1"></i>חסר מזהה פוסט</span>
                                            ) : slot.posted ? (
                                              slot.submitted_post?.status === 'pending' ? (
                                                <span className="text-warning"><i className="bi bi-hourglass-split me-1"></i>ממתין לפרסום</span>
                                              ) : slot.submitted_post?.verification_status === 'verified' ? (
                                                <span className="text-success"><i className="bi bi-check-circle-fill me-1"></i>פורסם ואומת</span>
                                              ) : slot.submitted_post?.verification_status === 'deleted' ? (
                                                <span className="text-danger"><i className="bi bi-x-circle-fill me-1"></i>נמחק מפייסבוק</span>
                                              ) : (
                                                <span className="text-success"><i className="bi bi-check-circle me-1"></i>פורסם</span>
                                              )
                                            ) : slot.is_error ? (
                                              <span className="text-danger"><i className="bi bi-x-circle me-1"></i>שגיאה</span>
                                            ) : (
                                              <span className="text-warning"><i className="bi bi-clock me-1"></i>ממתין</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                  {joinExtra > 0 && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-light-info p-0 d-flex align-items-center justify-content-center"
                                      style={{ width: '28px', height: '28px', minWidth: '28px', borderRadius: '50%', fontSize: '10px', fontWeight: 700, lineHeight: 1 }}
                                      title={`עוד ${joinExtra} הצטרפויות לקבוצות בשעה זו`}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setExpandedJoinCells(prev => {
                                          const next = new Set(prev)
                                          next.add(cellKey)
                                          return next
                                        })
                                      }}
                                    >
                                      +{joinExtra}
                                    </button>
                                  )}
                                  {isExpanded && joinSlots.length > JOIN_GROUP_CAP && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-light p-0 d-flex align-items-center justify-content-center"
                                      style={{ width: '28px', height: '28px', minWidth: '28px', borderRadius: '50%', fontSize: '10px', lineHeight: 1 }}
                                      title="כיווץ"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setExpandedJoinCells(prev => {
                                          const next = new Set(prev)
                                          next.delete(cellKey)
                                          return next
                                        })
                                      }}
                                    >
                                      −
                                    </button>
                                  )}
                                </div>
                                )
                              })()}
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          <div className="d-flex gap-4 mt-5 pt-5 border-top flex-wrap">
            <div className="d-flex align-items-center gap-2">
              <div className="symbol symbol-30px symbol-circle position-relative" style={{ opacity: 1 }}>
                <div className="symbol-label bg-light-primary border border-2 border-success rounded-circle" style={{ width: 30, height: 30 }}></div>
                <div
                  className="position-absolute rounded-circle bg-success d-flex align-items-center justify-content-center"
                  style={{
                    width: '12px',
                    height: '12px',
                    bottom: '0',
                    right: '0',
                    border: '2px solid white',
                    fontSize: '7px',
                    color: 'white'
                  }}
                >
                  <i className="bi bi-check"></i>
                </div>
              </div>
              <span className="text-muted fs-7">אומת</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="symbol symbol-30px symbol-circle" style={{ opacity: 1 }}>
                <div className="symbol-label bg-light-primary border border-2 border-success rounded-circle" style={{ width: 30, height: 30 }}></div>
              </div>
              <span className="text-muted fs-7">פורסם</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="symbol symbol-30px symbol-circle" style={{ opacity: 0.6 }}>
                <div className="symbol-label bg-light-primary rounded-circle" style={{ width: 30, height: 30 }}></div>
              </div>
              <span className="text-muted fs-7">ממתין</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="symbol symbol-30px symbol-circle">
                <div className="symbol-label bg-light-danger border border-2 border-danger rounded-circle" style={{ width: 30, height: 30 }}></div>
              </div>
              <span className="text-muted fs-7">שגיאה</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="symbol symbol-30px symbol-circle position-relative">
                <div className="symbol-label bg-light-success border border-2 border-success rounded-circle" style={{ width: 30, height: 30 }}></div>
                <div
                  className="position-absolute rounded-circle bg-danger d-flex align-items-center justify-content-center"
                  style={{
                    width: '12px',
                    height: '12px',
                    bottom: '0',
                    right: '0',
                    border: '2px solid white',
                    fontSize: '7px',
                    color: 'white'
                  }}
                >
                  <i className="bi bi-x"></i>
                </div>
              </div>
              <span className="text-muted fs-7">נמחק</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="symbol symbol-30px symbol-circle position-relative">
                <div className="symbol-label bg-light-success border border-2 border-success rounded-circle" style={{ width: 30, height: 30 }}></div>
                <div
                  className="position-absolute rounded-circle bg-danger"
                  style={{
                    width: '10px',
                    height: '10px',
                    bottom: '0',
                    right: '0',
                    border: '2px solid white'
                  }}
                />
              </div>
              <span className="text-muted fs-7">חסר מזהה</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="symbol symbol-30px symbol-circle">
                <div className="symbol-label bg-light-info border border-2 border-info rounded-circle" style={{ width: 30, height: 30 }}></div>
              </div>
              <span className="text-muted fs-7">הצטרפות לקבוצה</span>
            </div>
          </div>
        </div>
      </div>

      {/* Post Details Modal */}
      {selectedSlot && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedSlot.slot_type === 'join_group' ? 'פרטי הצטרפות לקבוצה' : 'פרטי פוסט מתוזמן'}
                </h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <div className="modal-body">
                {loadingDetails ? (
                  <div className="text-center py-5">
                    <span className="spinner-border text-primary"></span>
                    <p className="text-muted mt-2">טוען פרטים...</p>
                  </div>
                ) : slotDetails ? (
                  <div>
                    {/* Profile and Group Info */}
                    <div className="d-flex align-items-center mb-4">
                      <div className="symbol symbol-60px symbol-circle me-4">
                        <img
                          src={getProfilePic(slotDetails.user)}
                          alt={slotDetails.user?.name || 'Profile'}
                          className="rounded-circle"
                          onError={(e) => {
                            // Fallback to ui-avatars if Facebook picture fails
                            const name = slotDetails.user?.name || '?'
                            ;(e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&color=fff&size=60`
                          }}
                        />
                      </div>
                      <div>
                        <div className="fw-bold fs-4">{slotDetails.user?.name || 'Unknown Profile'}</div>
                        <div className="text-muted">
                          <i className="bi bi-people-fill me-1"></i>
                          {slotDetails.group?.group_id ? (
                            <a href={`https://www.facebook.com/groups/${slotDetails.group.group_id}`} target="_blank" rel="noopener noreferrer" className="text-muted text-hover-primary">
                              {slotDetails.group?.name || 'Unknown Group'}
                            </a>
                          ) : (
                            slotDetails.group?.name || 'Unknown Group'
                          )}
                        </div>
                      </div>
                      <div className="ms-auto">
                        {selectedSlot.slot_type === 'join_group' ? (
                          slotDetails.posted ? (
                            <span className="badge badge-lg badge-info">
                              <i className="bi bi-check-circle me-1"></i>הצטרף
                            </span>
                          ) : (
                            <span className="badge badge-lg badge-info">
                              <i className="bi bi-person-plus me-1"></i>ממתין להצטרפות
                            </span>
                          )
                        ) : isWarmupSkip(slotDetails) ? (
                          <span className="badge badge-lg badge-warning">
                            <i className="bi bi-moon-stars me-1"></i>דילוג חימום
                          </span>
                        ) : isHardBlocked(slotDetails) ? (
                          <span className="badge badge-lg zipper-blink-bg">
                            <i className="bi bi-slash-circle-fill me-1"></i>נחסם 1390008
                          </span>
                        ) : isLimitHit(slotDetails) ? (
                          <span className="badge badge-lg badge-danger">
                            <i className="bi bi-hand-thumbs-down-fill me-1"></i>הגביל את הפרופיל
                          </span>
                        ) : slotDetails.has_missing_post_id ? (
                          <span className="badge badge-lg badge-danger">
                            <i className="bi bi-exclamation-circle me-1"></i>חסר מזהה פוסט
                          </span>
                        ) : slotDetails.posted ? (
                          <span className="badge badge-lg badge-success">
                            <i className="bi bi-check-circle me-1"></i>פורסם
                          </span>
                        ) : slotDetails.is_error ? (
                          <span className="badge badge-lg badge-danger">
                            <i className="bi bi-x-circle me-1"></i>שגיאה
                          </span>
                        ) : (
                          <span className="badge badge-lg badge-warning">
                            <i className="bi bi-clock me-1"></i>ממתין
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Join Group Info */}
                    {selectedSlot.slot_type === 'join_group' && (
                      <div className="bg-light-info rounded p-4 mb-4 text-center">
                        <i className="bi bi-person-plus-fill fs-1 text-info mb-2"></i>
                        <div className="fw-bold fs-5 mb-1">הצטרפות לקבוצה</div>
                        <div className="text-muted">
                          {slotDetails.posted ? 'הפרופיל הצטרף לקבוצה בהצלחה' : 'הפרופיל מתוזמן להצטרף לקבוצה זו'}
                        </div>
                      </div>
                    )}

                    {/* Post Content */}
                    {selectedSlot.slot_type !== 'join_group' && slotDetails.post && (
                      <div className="bg-light rounded p-4 mb-4">
                        <div className="fw-bold mb-2">{slotDetails.post.name}</div>
                        {slotDetails.post.post_content && (
                          <div className="text-gray-700" style={{ whiteSpace: 'pre-wrap' }}>
                            {slotDetails.post.post_content}
                          </div>
                        )}
                        {slotDetails.post.media_url && (
                          <div className="mt-3">
                            <div className="d-flex flex-wrap gap-2">
                              {slotDetails.post.media_url.split(',').filter(Boolean).slice(0, 4).map((url: string, idx: number) => (
                                <img
                                  key={idx}
                                  src={watermarkPreviewSrc(url)}
                                  alt={`Media ${idx + 1}`}
                                  className="rounded"
                                  style={{ maxHeight: '100px', maxWidth: '150px', objectFit: 'cover' }}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none'
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Schedule Info */}
                    <div className="d-flex gap-4 text-muted fs-7">
                      {slotDetails.date && (
                        <div>
                          <i className="bi bi-calendar me-1"></i>
                          {slotDetails.date}
                        </div>
                      )}
                      <div>
                        <i className="bi bi-clock me-1"></i>
                        {slotDetails.time_slot}:00
                      </div>
                      {slotDetails.campaign && (
                        <div>
                          <i className="bi bi-folder me-1"></i>
                          {slotDetails.campaign.name}
                        </div>
                      )}
                    </div>

                    {/* Facebook Post Embed */}
                    {selectedSlot.slot_type !== 'join_group' && slotDetails.posted && slotDetails.submitted_post?.facebook_post_id && slotDetails.group?.group_id && (
                      <div className="mt-4">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div className="text-success">
                            <i className="bi bi-check-circle me-2"></i>
                            <span className="fw-bold">פורסם בהצלחה</span>
                          </div>
                          <a
                            href={`https://www.facebook.com/groups/${slotDetails.group.group_id}/posts/${slotDetails.submitted_post.facebook_post_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="bi bi-box-arrow-up-right me-1"></i>
                            פתח בפייסבוק
                          </a>
                        </div>
                        <div className="border rounded overflow-hidden" style={{ maxHeight: '500px' }}>
                          <iframe
                            src={`https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(`https://www.facebook.com/groups/${slotDetails.group.group_id}/posts/${slotDetails.submitted_post.facebook_post_id}`)}&show_text=true&width=500`}
                            width="100%"
                            height="450"
                            style={{ border: 'none', overflow: 'hidden' }}
                            scrolling="no"
                            frameBorder="0"
                            allowFullScreen={true}
                            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                          />
                        </div>
                      </div>
                    )}

                    {/* Verification Status Indicator */}
                    {selectedSlot.slot_type !== 'join_group' && slotDetails.posted && slotDetails.submitted_post?.facebook_post_id && (
                      <div className={`alert mt-4 ${
                        slotDetails.submitted_post.verification_status === 'verified' ? 'alert-success' :
                        slotDetails.submitted_post.verification_status === 'deleted' ? 'alert-danger' :
                        slotDetails.submitted_post.verification_status === 'error' ? 'alert-warning' :
                        'alert-secondary'
                      }`}>
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <i className={`fs-3 me-3 ${
                              slotDetails.submitted_post.verification_status === 'verified' ? 'bi bi-check-circle-fill text-success' :
                              slotDetails.submitted_post.verification_status === 'deleted' ? 'bi bi-x-circle-fill text-danger' :
                              slotDetails.submitted_post.verification_status === 'error' ? 'bi bi-exclamation-triangle-fill text-warning' :
                              'bi bi-question-circle text-secondary'
                            }`}></i>
                            <div>
                              <div className="fw-bold mb-1">
                                {slotDetails.submitted_post.verification_status === 'verified' ? 'הפוסט אומת - קיים בפייסבוק' :
                                 slotDetails.submitted_post.verification_status === 'deleted' ? 'הפוסט נמחק מפייסבוק' :
                                 slotDetails.submitted_post.verification_status === 'error' ? 'שגיאה באימות הפוסט' :
                                 'ממתין לאימות'}
                              </div>
                              {slotDetails.submitted_post.verification_date && (
                                <div className="text-muted fs-7">
                                  נבדק: {new Date(slotDetails.submitted_post.verification_date).toLocaleString('he-IL')}
                                </div>
                              )}
                              {slotDetails.submitted_post.verification_error && (
                                <div className="text-muted fs-7">
                                  שגיאה: {slotDetails.submitted_post.verification_error}
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleVerifyPost}
                            disabled={verifyingPost}
                          >
                            {verifyingPost ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1"></span>
                                בודק...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-arrow-repeat me-1"></i>
                                בדוק שוב
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Verify Post Result */}
                    {verifyPostResult && (
                      <div className={`alert ${verifyPostResult.success ? 'alert-success' : 'alert-danger'} mt-4`}>
                        <i className={`bi ${verifyPostResult.success ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-2`}></i>
                        {verifyPostResult.message}
                      </div>
                    )}

                    {/* Warning for missing post ID */}
                    {selectedSlot.slot_type !== 'join_group' && slotDetails.posted && slotDetails.has_missing_post_id && (
                      <div className="alert alert-danger mt-4">
                        <div className="d-flex align-items-center">
                          <i className="bi bi-exclamation-triangle fs-3 me-3 text-danger"></i>
                          <div>
                            <div className="fw-bold mb-1">חסר מזהה פוסט</div>
                            <div className="text-gray-700">הפוסט סומן כפורסם אך לא התקבל מזהה מפייסבוק. ייתכן שהפרסום נכשל.</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Warm-up skip (intentional throttle for a recently-unblocked profile) */}
                    {isWarmupSkip(slotDetails) && (
                      <div className="alert alert-warning mt-4">
                        <div className="d-flex align-items-center">
                          <i className="bi bi-moon-stars fs-3 me-3 text-warning"></i>
                          <div>
                            <div className="fw-bold mb-1">דילוג חימום (לא שגיאה)</div>
                            <div className="text-gray-700">הפרופיל יצא לאחרונה מחסימה ונמצא בתקופת חימום — חלק מהפוסטים מדולגים בכוונה כדי להימנע מחסימה חוזרת.</div>
                            <div className="text-muted fs-8 mt-1">{slotDetails.error_message}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* FB hard block (1390008) — account temporarily blocked. Flashing red. */}
                    {isHardBlocked(slotDetails) && (
                      <div className="alert mt-4 zipper-blink-bg">
                        <div className="d-flex align-items-center">
                          <i className="bi bi-slash-circle-fill fs-3 me-3 text-white"></i>
                          <div className="text-white">
                            <div className="fw-bold mb-1">נחסם ע״י פייסבוק (קוד 1390008)</div>
                            <div>פייסבוק חסם זמנית את הפרופיל מפרסום. הפוסט לא נשלח — הפרופיל יחזור לפרסם אחרי שהחסימה תתפוגג.</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* This post is what tripped the limit (FB rate-limit response) */}
                    {!isHardBlocked(slotDetails) && isLimitHit(slotDetails) && (
                      <div className="alert alert-danger mt-4">
                        <div className="d-flex align-items-center">
                          <i className="bi bi-hand-thumbs-down-fill fs-3 me-3 text-danger"></i>
                          <div>
                            <div className="fw-bold mb-1">הפוסט הזה הגביל את הפרופיל</div>
                            <div className="text-gray-700">פייסבוק החזיר חסימת קצב על הניסיון הזה — זה הפוסט שגרם להגבלת הפרופיל.</div>
                            <div className="text-muted fs-8 mt-1">{slotDetails.error_message}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Error Message from Facebook */}
                    {slotDetails.is_error && !isWarmupSkip(slotDetails) && !isHardBlocked(slotDetails) && !isLimitHit(slotDetails) && slotDetails.error_message && (
                      <div className="alert alert-danger mt-4">
                        <div className="d-flex align-items-center">
                          <i className="bi bi-exclamation-triangle fs-3 me-3 text-danger"></i>
                          <div>
                            <div className="fw-bold mb-1">שגיאה בפרסום</div>
                            <div className="text-gray-700">{slotDetails.error_message}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Logs */}
                    {slotDetails.logs && slotDetails.logs.length > 0 && (
                      <div className="mt-4">
                        <h6 className="fw-bold mb-3">
                          <i className="bi bi-journal-text me-2"></i>
                          לוג פעולות
                        </h6>
                        <div className="bg-light rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {slotDetails.logs.map((log: any, index: number) => (
                            <div
                              key={log._id || index}
                              className={`d-flex align-items-start gap-2 ${index > 0 ? 'mt-2 pt-2 border-top' : ''}`}
                            >
                              <div className={`badge ${log.is_error ? 'bg-danger' : 'bg-secondary'} mt-1`} style={{ minWidth: '60px' }}>
                                {log.action || 'log'}
                              </div>
                              <div className="flex-grow-1">
                                <div className={`fs-7 ${log.is_error ? 'text-danger' : 'text-gray-700'}`} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {log.message}
                                </div>
                                <div className="text-muted fs-8 mt-1">
                                  {new Date(log.created_at).toLocaleString('he-IL')}
                                  {log.user && ` - ${log.user.name}`}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Success/Error Messages */}
                    {postNowSuccess && (
                      <div className="alert alert-success mt-4">
                        <i className="bi bi-check-circle me-2"></i>
                        {postNowSuccess}
                        {postUrl && (
                          <div className="mt-2">
                            <a href={postUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-success">
                              <i className="bi bi-box-arrow-up-right me-1"></i>
                              צפה בפוסט בפייסבוק
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                    {postNowError && (
                      <div className="alert alert-danger mt-4">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {postNowError}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">
                    לא נמצאו פרטים
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={closeModal}>
                  סגור
                </button>
                {selectedSlot.slot_type !== 'join_group' && slotDetails && !slotDetails.posted && !slotDetails.is_error && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handlePostNow}
                    disabled={postingNow}
                  >
                    {postingNow ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        מפרסם...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-2"></i>
                        פרסם עכשיו
                      </>
                    )}
                  </button>
                )}
                {selectedSlot.slot_type !== 'join_group' && slotDetails && slotDetails.is_error && (
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={handleRetry}
                    disabled={postingNow}
                  >
                    {postingNow ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        מנסה שוב...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-repeat me-2"></i>
                        נסה שוב
                      </>
                    )}
                  </button>
                )}
                {selectedSlot.slot_type === 'join_group' && slotDetails && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleJoinGroupNow}
                    disabled={joiningGroupNow}
                  >
                    {joiningGroupNow ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        מצטרף...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-person-plus me-2"></i>
                        הצטרף עכשיו
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create from Post Modal */}
      {showMarketplaceModal && slotDetails?.post && (
        <CreateFromPostModal
          show={showMarketplaceModal}
          onClose={() => setShowMarketplaceModal(false)}
          onSuccess={() => {
            setShowMarketplaceModal(false)
            // Could show a success toast here
          }}
          postData={{
            post_id: slotDetails.post._id,
            title: slotDetails.post.name,
            content: slotDetails.post.post_content,
            media_urls: slotDetails.post.media_url
              ? slotDetails.post.media_url.split(',').filter(Boolean).map((url: string) => url.trim())
              : [],
            campaign_id: slotDetails.campaign?._id,
            campaign_name: slotDetails.campaign?.name,
            profile_id: slotDetails.user?._id
          }}
        />
      )}

      {/* Reschedule All Campaigns Modal - Pirsomat Style */}
      {/* Admin: per-profile override of the global 7/day join cap */}
      {/* Whole-account join cap */}
      {bulkJoinCapOpen && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => !bulkJoinCapBusy && setBulkJoinCapOpen(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header bg-warning text-dark d-block">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-bold"><i className="bi bi-lightning-charge me-2"></i>מכסת הצטרפות יומית — כל הפרופילים</h5>
                  <button className="btn-close" onClick={() => setBulkJoinCapOpen(false)} disabled={bulkJoinCapBusy}></button>
                </div>
                <p className="mb-0 opacity-75 fs-7 mt-1">
                  אדמין בלבד: קובע כמה קבוצות כל פרופיל בחשבון רשאי להצטרף אליהן ביום, וגובר על מכסת 7 ההצטרפויות ועל קצב ההרצה
                </p>
              </div>
              <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {/* Quick way to give everyone the same number, then adjust individuals below. */}
                <div className="d-flex align-items-end gap-2 mb-3 pb-3 border-bottom">
                  <div className="flex-grow-1">
                    <label className="form-label fw-semibold mb-1 fs-8">קבע לכולם בבת אחת</label>
                    <input
                      type="number" min={1} max={100} className="form-control form-control-sm"
                      value={bulkJoinCapValue}
                      onChange={(e) => { setBulkJoinCapValue(e.target.value); setBulkJoinCapError(null) }}
                      placeholder="לדוגמה: 20" disabled={bulkJoinCapBusy}
                    />
                  </div>
                  <button className="btn btn-sm btn-warning" onClick={() => applyBulkJoinCap(false)} disabled={bulkJoinCapBusy || !bulkJoinCapValue}>
                    {bulkJoinCapBusy ? '…' : 'החל על כולם'}
                  </button>
                  <button className="btn btn-sm btn-light-danger" onClick={() => applyBulkJoinCap(true)} disabled={bulkJoinCapBusy}>
                    בטל לכולם
                  </button>
                </div>

                <div className="text-muted fs-8 mb-2">
                  מכסה ריקה = הכלל הרגיל (7 ליום, או 30 ליום לפרופיל שעדיין בפחות מ-50 קבוצות).
                </div>

                <table className="table table-row-bordered align-middle gy-2 mb-0">
                  <thead>
                    <tr className="fw-bold text-muted fs-8">
                      <th>פרופיל</th>
                      <th className="text-center">קבוצות</th>
                      <th className="text-center">בפועל היום</th>
                      <th style={{ width: 110 }}>מכסה ידנית</th>
                      <th style={{ width: 64 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {profileStats.map((p) => {
                      const draft = rowJoinCap[p._id]
                      const current = p.join_cap_override ? String(p.join_cap_override) : ''
                      const value = draft !== undefined ? draft : current
                      const dirty = value !== current
                      return (
                        <tr key={p._id}>
                          <td className="fs-7">
                            <div className="d-flex align-items-center gap-2">
                              {/* Graph API with the app token, same as the profile cards
                                  above — the stored profile_picture_url expires, so it's
                                  only the fallback. */}
                              <img
                                src={p.facebook_id
                                  ? `https://graph.facebook.com/${p.facebook_id}/picture?type=normal&access_token=${process.env.REACT_APP_FB_ACCESS_TOKEN}`
                                  : p.profile_picture_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name)}&background=random&color=fff&size=28`}
                                alt={p.name}
                                className="rounded-circle flex-shrink-0"
                                style={{ width: '28px', height: '28px', objectFit: 'cover' }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name)}&background=random&color=fff&size=28`
                                }}
                              />
                              <span style={{ minWidth: 0 }}>
                                {p.name}
                                {p.parent_name ? <span className="text-muted fs-8"> · {p.parent_name}</span> : null}
                              </span>
                            </div>
                          </td>
                          <td className="text-center fs-8 text-muted">{p.groups_following}</td>
                          <td className="text-center fs-8 fw-bold">{p.effective_join_cap ?? '—'}</td>
                          <td>
                            <input
                              type="number" min={1} max={100}
                              className="form-control form-control-sm"
                              value={value}
                              placeholder="רגיל"
                              disabled={rowJoinCapBusy === p._id}
                              onChange={(e) => setRowJoinCap((prev) => ({ ...prev, [p._id]: e.target.value }))}
                            />
                          </td>
                          <td>
                            <button
                              className={`btn btn-sm ${dirty ? 'btn-warning' : 'btn-light'}`}
                              disabled={!dirty || rowJoinCapBusy === p._id}
                              onClick={() => saveRowJoinCap(p._id, value)}
                              title="שמור לפרופיל הזה"
                            >
                              {rowJoinCapBusy === p._id ? '…' : 'שמור'}
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

                {bulkJoinCapError && <div className="alert alert-danger py-2 px-3 mt-3 mb-0 fs-7">{bulkJoinCapError}</div>}
                {bulkJoinCapDone && <div className="alert alert-success py-2 px-3 mt-3 mb-0 fs-7">{bulkJoinCapDone}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-light" onClick={() => setBulkJoinCapOpen(false)} disabled={bulkJoinCapBusy}>סגור</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {joinCapModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => !joinCapBusy && setJoinCapModal(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content rounded-4 overflow-hidden" dir="rtl" style={{ textAlign: 'right' }}>
              <div style={{ background: 'linear-gradient(to left, #f59e0b, #ef4444)', color: 'white', padding: '1rem 1.5rem' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-bold"><i className="bi bi-lightning-charge me-2"></i>מכסת הצטרפות יומית — {joinCapModal.name}</h5>
                  <button className="btn-close btn-close-white" onClick={() => setJoinCapModal(null)}></button>
                </div>
                <p className="mb-0 opacity-75 fs-7 mt-1">אדמין בלבד: עוקף את מכסת 7 ההצטרפויות ליום עבור הפרופיל הזה</p>
              </div>
              <div className="modal-body">
                <div className="d-flex flex-wrap gap-3 mb-3">
                  <div className="rounded-3 px-3 py-2 flex-grow-1" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div className="text-muted fs-8">חבר כרגע ב</div>
                    <div className="fw-bold">{joinCapModal.groups_following} קבוצות</div>
                  </div>
                  <div className="rounded-3 px-3 py-2 flex-grow-1" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div className="text-muted fs-8">מכסה בפועל היום</div>
                    <div className="fw-bold">{joinCapModal.effective_join_cap ?? '—'} ליום</div>
                  </div>
                </div>

                <div className="rounded-3 p-3 mb-3 fs-8" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
                  ברירת המחדל: פרופיל שחבר בפחות מ-50 קבוצות מצטרף ב-30 ליום (האצה ראשונית), ומעל זה יורד ל-7 ליום.
                  ערך ידני כאן גובר על שניהם — גם על ההאצה וגם על מכסת ה-7.
                </div>

                <label className="form-label fw-bold fs-7">הצטרפויות ליום (1–100)</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  autoFocus
                  className="form-control form-control-sm mb-2"
                  placeholder={`ללא עקיפה (${joinCapModal.effective_join_cap ?? 7} ליום לפי הכללים)`}
                  value={joinCapValue}
                  onChange={(e) => setJoinCapValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !joinCapBusy) handleSaveJoinCapOverride(false) }}
                />
                <div className="text-muted fs-8">
                  {joinCapModal.join_cap_override
                    ? <>כרגע מוגדרת עקיפה של <span className="fw-bold">{joinCapModal.join_cap_override}</span> ליום. "בטל עקיפה" יחזיר את הפרופיל לכללים הרגילים.</>
                    : 'אין עקיפה — הפרופיל מתנהג לפי הכללים הרגילים.'}
                  <span className="d-block mt-1">ערכים גבוהים מגדילים סיכון לחסימת פייסבוק. משפיע על התזמון הבא, לא על הצטרפויות שכבר תוזמנו להיום.</span>
                </div>
                {joinCapError && <div className="text-danger fs-8 mt-2">{joinCapError}</div>}
              </div>
              <div className="modal-footer border-0">
                <button className="btn btn-light" onClick={() => setJoinCapModal(null)} disabled={joinCapBusy}>ביטול</button>
                {!!joinCapModal.join_cap_override && (
                  <button className="btn btn-light-danger" onClick={() => handleSaveJoinCapOverride(true)} disabled={joinCapBusy}>
                    בטל עקיפה
                  </button>
                )}
                <button className="btn btn-warning" onClick={() => handleSaveJoinCapOverride(false)} disabled={joinCapBusy || !joinCapValue}>
                  {joinCapBusy ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-check2 me-1"></i>}
                  שמור
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {blacklistModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => !blacklistBusy && setBlacklistModal(null)}>
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content rounded-4 overflow-hidden" dir="rtl" style={{ textAlign: 'right' }}>
              <div style={{ background: 'linear-gradient(to left, #ef4444, #b91c1c)', color: 'white', padding: '1rem 1.5rem' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-bold"><i className="bi bi-slash-circle me-2"></i>קבוצות חסומות — {blacklistModal.name}</h5>
                  <button className="btn-close btn-close-white" onClick={() => setBlacklistModal(null)}></button>
                </div>
                <p className="mb-0 opacity-75 fs-7 mt-1">
                  קבוצות שהבוט סימן כלא-זמינות לפרופיל הזה אחרי כשל פרסום. התזמון מדלג עליהן לחלוטין.
                </p>
              </div>
              <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {blacklistError && <div className="alert alert-danger fs-8 py-2">{blacklistError}</div>}
                {blacklistLoading ? (
                  <div className="text-center py-6"><span className="spinner-border text-danger"></span></div>
                ) : blacklistGroups.length === 0 ? (
                  <div className="text-center text-muted py-6">
                    <i className="bi bi-check-circle fs-2x text-success d-block mb-2"></i>
                    אין קבוצות חסומות לפרופיל הזה.
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-8 text-muted">
                        {blacklistGroups.length} קבוצות חסומות · <span className="text-danger fw-bold">{blacklistGroups.filter(g => g.in_campaign).length}</span> מהן נמצאות בקמפיינים פעילים ולכן עולות לך בפוסטים.
                      </div>
                      {blacklistCanEdit && (
                        <button className="btn btn-sm btn-light-danger py-1 px-3" onClick={handleClearBlacklist} disabled={!!blacklistBusy}>
                          {blacklistBusy === 'all' ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-x-circle me-1 fs-9"></i>}
                          בטל חסימה לכולן
                        </button>
                      )}
                    </div>
                    <div className="d-flex flex-column gap-2">
                      {blacklistGroups.map(g => (
                        <div key={g._id} className="border rounded p-2 d-flex align-items-start gap-2" style={{ borderColor: g.in_campaign ? '#fca5a5' : '#e4e6ef' }}>
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              {g.group_id ? (
                                <a href={`https://facebook.com/groups/${g.group_id}`} target="_blank" rel="noreferrer" className="fw-bold text-gray-800 text-truncate" style={{ maxWidth: '320px' }}>
                                  {g.name}
                                </a>
                              ) : (
                                <span className="fw-bold text-gray-800 text-truncate" style={{ maxWidth: '320px' }}>{g.name}</span>
                              )}
                              {g.in_campaign && <span className="badge badge-light-danger" style={{ fontSize: '9px' }}>בקמפיין</span>}
                              {!g.is_member && <span className="badge badge-light-warning" style={{ fontSize: '9px' }}>לא חבר בקבוצה</span>}
                              {g.is_for_sale_group && <span className="badge badge-light-info" style={{ fontSize: '9px' }}>קבוצת מכירה</span>}
                            </div>
                            {g.last_error && (
                              <div
                                className={`fs-9 mt-1 ${g.last_error_matches_group ? 'text-danger' : 'text-muted'}`}
                                style={{ wordBreak: 'break-word' }}
                                title={g.last_error_matches_group ? 'השגיאה האחרונה בקבוצה הזו' : 'השגיאה האחרונה של הפרופיל בסלוט של קבוצה זו — ייתכן שהיא מתייחסת לקבוצה אחרת'}
                              >
                                {!g.last_error_matches_group && <span className="me-1">(כללי)</span>}
                                {g.last_error}
                                {g.last_error_at && <span className="text-muted"> · {new Date(g.last_error_at).toLocaleDateString('he-IL')}</span>}
                              </div>
                            )}
                            <div className="fs-9 text-muted mt-1">
                              {g.posts_count ? <>פורסמו {g.posts_count} פוסטים בקבוצה{g.last_posted ? ` · אחרון ${new Date(g.last_posted).toLocaleDateString('he-IL')}` : ''}</> : 'מעולם לא פורסם בקבוצה זו'}
                            </div>
                          </div>
                          {blacklistCanEdit && (
                            <button
                              className="btn btn-sm btn-light-success py-1 px-3 flex-shrink-0"
                              onClick={() => handleUnblacklistGroup(g._id)}
                              disabled={!!blacklistBusy}
                              title="החזרת הקבוצה לתזמון עבור הפרופיל הזה"
                            >
                              {blacklistBusy === g._id ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-arrow-counterclockwise me-1 fs-9"></i>בטל חסימה</>}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="rounded-3 p-3 mt-3 fs-8" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
                      ביטול חסימה משפיע על התזמון הבא בלבד — הרץ "תזמן מחדש" כדי שהפוסטים ייווצרו כבר היום.
                      אם הפרופיל באמת חסום בקבוצה בפייסבוק, הבוט יחסום אותה שוב אחרי הכשל הבא.
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer border-0">
                <button className="btn btn-light" onClick={() => setBlacklistModal(null)} disabled={!!blacklistBusy}>סגור</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showJoinForProfile && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => !jfpBusy && setShowJoinForProfile(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content rounded-4 overflow-hidden" dir="rtl" style={{ textAlign: 'right' }}>
              <div style={{ background: 'linear-gradient(to left, #0ea5e9, #6366f1)', color: 'white', padding: '1rem 1.5rem' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-bold"><i className="bi bi-people-fill me-2"></i>הצטרפות לקבוצות לפרופילים</h5>
                  <button className="btn-close btn-close-white" onClick={() => setShowJoinForProfile(false)}></button>
                </div>
                <p className="mb-0 opacity-75 fs-7 mt-1">כל פרופיל שתבחר יצטרף למספר הקבוצות שתבחר — מכל קמפיין מסומן, מפוזר אוטומטית על היום</p>
              </div>
              <div className="modal-body">
                {/* Profiles (multi-select) */}
                {(() => {
                  const connected = profileStats.filter(p => p.facebook_id)
                  const allIds = connected.map(p => p._id)
                  const allOn = allIds.length > 0 && jfpProfiles.length === allIds.length
                  return (
                    <>
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <label className="form-label fw-bold fs-7 mb-0">פרופילים ({jfpProfiles.length} נבחרו)</label>
                        <button type="button" className="btn btn-sm btn-link p-0 fs-8" onClick={() => setJfpProfiles(allOn ? [] : allIds)}>
                          {allOn ? 'נקה הכל' : 'בחר הכל'}
                        </button>
                      </div>
                      <div className="border rounded mb-3" style={{ maxHeight: 170, overflowY: 'auto' }}>
                        {connected.map(p => (
                          <label key={p._id} className="d-flex align-items-center gap-2 px-3 py-2 border-bottom mb-0" style={{ cursor: 'pointer' }}>
                            <input type="checkbox" className="form-check-input mt-0" checked={jfpProfiles.includes(p._id)} onChange={() => toggleJfpProfile(p._id)} />
                            <span className="fs-7">{p.name}</span>
                            <span className="text-muted fs-8 ms-auto">{p.groups_following} קבוצות</span>
                          </label>
                        ))}
                        {connected.length === 0 && <div className="text-muted fs-8 px-3 py-2">אין פרופילים מחוברים</div>}
                      </div>
                    </>
                  )
                })()}
                {/* Campaigns (multi-select) */}
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <label className="form-label fw-bold fs-7 mb-0">קמפיינים ({jfpCampaignIds.length} נבחרו)</label>
                  <button type="button" className="btn btn-sm btn-link p-0 fs-8"
                    onClick={() => setJfpCampaignIds(jfpCampaigns.length > 0 && jfpCampaignIds.length === jfpCampaigns.length ? [] : jfpCampaigns.map(c => c._id))}>
                    {jfpCampaigns.length > 0 && jfpCampaignIds.length === jfpCampaigns.length ? 'נקה הכל' : 'בחר הכל'}
                  </button>
                </div>
                <div className="border rounded mb-3" style={{ maxHeight: 170, overflowY: 'auto' }}>
                  {jfpCampaigns.map(c => (
                    <label key={c._id} className="d-flex align-items-center gap-2 px-3 py-2 border-bottom mb-0" style={{ cursor: 'pointer' }}>
                      <input type="checkbox" className="form-check-input mt-0" checked={jfpCampaignIds.includes(c._id)} onChange={() => toggleJfpCampaign(c._id)} />
                      <span className="fs-7">{c.name}</span>
                    </label>
                  ))}
                  {jfpCampaigns.length === 0 && <div className="text-muted fs-8 px-3 py-2">טוען קמפיינים…</div>}
                </div>
                <label className="form-label fw-bold fs-7">כמה קבוצות להצטרף מכל קמפיין (לכל פרופיל)</label>
                <input type="number" min={1} max={500} className="form-control form-control-sm mb-2" value={jfpCount} onChange={(e) => setJfpCount(parseInt(e.target.value || '0', 10))} />
                <div className="text-muted fs-8">
                  מצטרף רק לקבוצות שהפרופיל עדיין לא חבר בהן, מפוזר על שעות היום. כלי ידני — ניתן לחרוג מעבר ל-7 ההצטרפויות הרגילות ליום (זהירות מחסימות פייסבוק).
                  {jfpProfiles.length > 0 && jfpCampaignIds.length > 0 && jfpCount > 0 && (
                    <span className="d-block mt-1 fw-bold text-gray-700">עד {jfpProfiles.length * jfpCampaignIds.length * jfpCount} הצטרפויות בסך הכל ({jfpProfiles.length} פרופילים × {jfpCampaignIds.length} קמפיינים × {jfpCount}).</span>
                  )}
                </div>
              </div>
              <div className="modal-footer border-0">
                <button className="btn btn-light" onClick={() => setShowJoinForProfile(false)} disabled={jfpBusy}>ביטול</button>
                <button className="btn btn-primary" onClick={submitJoinForProfile} disabled={jfpBusy || !jfpProfiles.length || !jfpCampaignIds.length || !jfpCount}>
                  {jfpBusy ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-calendar-plus me-1"></i>}
                  תזמן הצטרפות
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRescheduleModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content rounded-4 overflow-hidden" dir="rtl" style={{ textAlign: 'right' }}>
              {/* Header - Orange gradient like pirsomat */}
              <div style={{ background: 'linear-gradient(to left, #f97316, #ef4444)', color: 'white', padding: '1rem 1.5rem' }}>
                <h5 className="modal-title fw-bold mb-1">תצוגה מקדימה של התזמון</h5>
                <p className="mb-0 opacity-75 fs-7">תאריך: {reschedulePreview?.date || new Date().toLocaleDateString('he-IL')}</p>
              </div>

              <div className="modal-body p-4" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {loadingReschedulePreview ? (
                  <div className="text-center py-10">
                    <span className="spinner-border spinner-border-lg text-primary"></span>
                    <p className="text-muted mt-3">מחשב...</p>
                  </div>
                ) : reschedulePreview ? (
                  <div>
                    {/* Full-capacity duplicate mode (admin only) */}
                    {(isAdminSwitch || reschedulePreview.allow_duplicate_posts) && (
                      <div className="rounded-3 p-3 mb-4 d-flex align-items-center justify-content-between gap-3" style={{
                        backgroundColor: reschedulePreview.allow_duplicate_posts ? '#f0fdf4' : '#f8fafc',
                        border: `1px solid ${reschedulePreview.allow_duplicate_posts ? '#bbf7d0' : '#e2e8f0'}`
                      }}>
                        <div>
                          <div className="fw-bold fs-7" style={{ color: reschedulePreview.allow_duplicate_posts ? '#15803d' : '#475569' }}>
                            <i className={`bi ${reschedulePreview.allow_duplicate_posts ? 'bi-rocket-takeoff-fill' : 'bi-rocket-takeoff'} me-2`}></i>
                            מצב תפוקה מלאה {reschedulePreview.allow_duplicate_posts ? '— פעיל' : '— כבוי'}
                          </div>
                          <div className="text-muted fs-8 mt-1">
                            אותו פוסט יכול להתפרסם שוב באותה קבוצה — תמיד על ידי פרופיל אחר. מנצל את מלוא מכסת הפרופילים.
                          </div>
                        </div>
                        {isAdminSwitch && (
                          <button
                            className={`btn btn-sm ${reschedulePreview.allow_duplicate_posts ? 'btn-light-danger' : 'btn-success'}`}
                            onClick={handleToggleDuplicateMode}
                            disabled={togglingDuplicateMode}
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {togglingDuplicateMode ? <span className="spinner-border spinner-border-sm" /> :
                              reschedulePreview.allow_duplicate_posts ? 'כבה' : 'הפעל'}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Stats Grid - 4 columns */}
                    <div className="row g-3 mb-4">
                      <div className="col-3">
                        <div className="rounded-3 p-3 text-center" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                          <div className="fs-2 fw-bold" style={{ color: '#dc2626' }}>{reschedulePreview.willDelete || 0}</div>
                          <div className="fw-semibold fs-7" style={{ color: '#b91c1c' }}>יימחקו</div>
                          <div className="text-muted fs-9 mt-1">פוסטים שלא פורסמו</div>
                        </div>
                      </div>
                      <div className="col-3">
                        <div className="rounded-3 p-3 text-center" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                          <div className="fs-2 fw-bold" style={{ color: '#16a34a' }}>{reschedulePreview.estimatedCreate || 0}</div>
                          <div className="fw-semibold fs-7" style={{ color: '#15803d' }}>ייווצרו</div>
                          <div className="text-muted fs-9 mt-1">פוסטים חדשים</div>
                        </div>
                      </div>
                      <div className="col-3">
                        <div className="rounded-3 p-3 text-center" style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                          <div className="fs-2 fw-bold" style={{ color: '#2563eb' }}>{reschedulePreview.willKeep || 0}</div>
                          <div className="fw-semibold fs-7" style={{ color: '#1d4ed8' }}>יישארו</div>
                          <div className="text-muted fs-9 mt-1">כבר פורסמו</div>
                        </div>
                      </div>
                      <div className="col-3">
                        <div className="rounded-3 p-3 text-center" style={{ backgroundColor: '#fef3c7', border: '1px solid #fcd34d' }}>
                          <div className="fs-2 fw-bold" style={{ color: '#d97706' }}>{reschedulePreview.totalJoinGroups || 0}</div>
                          <div className="fw-semibold fs-7" style={{ color: '#b45309' }}>הצטרפות</div>
                          <div className="text-muted fs-9 mt-1">קבוצות להצטרפות</div>
                        </div>
                      </div>
                    </div>

                    {/* Group Schedule Warning */}
                    {reschedulePreview.campaigns?.some((c: any) => c.warnings?.some((w: any) => w.type === 'schedule_limited')) && (
                      <div className="rounded-3 p-3 mb-4 d-flex align-items-start gap-2" style={{ backgroundColor: '#fef3c7', border: '1px solid #fcd34d' }}>
                        <i className="bi bi-clock-history fs-4" style={{ color: '#d97706' }}></i>
                        <div>
                          <div className="fw-bold fs-7" style={{ color: '#92400e' }}>הגדרות תזמון קבוצות מגבילות את הפרסום</div>
                          {reschedulePreview.campaigns.map((c: any) => {
                            const schedWarn = c.warnings?.find((w: any) => w.type === 'schedule_limited');
                            if (!schedWarn) return null;
                            return (
                              <div key={c._id} className="fs-8 mt-1" style={{ color: '#92400e' }}>
                                {c.name}: {schedWarn.count} מתוך {schedWarn.totalGroups} קבוצות לא זמינות כרגע (יום או שעה לא מוגדרים)
                              </div>
                            );
                          })}
                          <div className="fs-8 mt-2" style={{ color: '#78350f' }}>
                            <a href="/group-scheduling" style={{ color: '#78350f', textDecoration: 'underline' }}>לניהול תזמון קבוצות</a>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Time Slots Distribution Per Profile */}
                    {reschedulePreview.profileTimeSlots && reschedulePreview.profileTimeSlots.length > 0 && (
                      <div className="rounded-3 p-3 mb-4" style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                        <h6 className="fw-semibold text-gray-700 mb-3 fs-7">פיזור פוסטים לפי שעות:</h6>
                        {reschedulePreview.profileTimeSlots.map((profile: any) => (
                          <div key={profile._id} className="mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <img
                                src={profile.facebook_id ? `https://graph.facebook.com/${profile.facebook_id}/picture?type=small&access_token=${process.env.REACT_APP_FB_ACCESS_TOKEN}` : '/media/avatars/blank.png'}
                                alt={profile.name}
                                style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                                onError={(e) => { (e.target as HTMLImageElement).src = '/media/avatars/blank.png' }}
                              />
                              <span className="fw-semibold fs-8 text-gray-700">{profile.name}</span>
                              <span className="badge badge-light-primary fs-9">
                                {profile.slots.reduce((sum: number, s: any) => sum + s.count, 0)} פוסטים
                              </span>
                            </div>
                            <div className="d-flex flex-wrap gap-2 me-4">
                              {profile.slots.map((slot: any) => (
                                <div
                                  key={slot.hour}
                                  className="d-flex align-items-center gap-1 px-2 py-1 rounded-2"
                                  style={{ backgroundColor: '#dbeafe', border: '1px solid #93c5fd' }}
                                >
                                  <span className="fw-medium fs-8" style={{ color: '#1e40af' }}>
                                    {String(slot.hour).padStart(2, '0')}:00
                                  </span>
                                  <span className="fw-bold fs-8" style={{ color: '#1d4ed8' }}>
                                    ({slot.count})
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Summary Section */}
                    <div className="rounded-3 p-4 mb-4" style={{ backgroundColor: '#f9fafb' }}>
                      <h6 className="fw-semibold text-gray-700 mb-3">סיכום</h6>
                      <div className="d-flex flex-wrap gap-4 fs-7">
                        <div>
                          <span className="text-muted">קמפיינים:</span>
                          <span className="fw-semibold text-gray-800 ms-2">{reschedulePreview.totalCampaigns || 0}</span>
                        </div>
                        <div>
                          <span className="text-muted">פרופילים בשימוש:</span>
                          <span className="fw-semibold text-gray-800 ms-2">
                            {reschedulePreview.campaigns?.reduce((acc: number, c: any) => {
                              const profileIds = new Set(c.profileUsage?.map((p: any) => p._id) || []);
                              return acc + profileIds.size;
                            }, 0) || 0}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted">קבוצות בשימוש:</span>
                          <span className="fw-semibold text-gray-800 ms-2">
                            {reschedulePreview.campaigns?.reduce((acc: number, c: any) => acc + (c.groupsCount || 0), 0) || 0}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Campaign Breakdown */}
                    {reschedulePreview.campaigns && reschedulePreview.campaigns.length > 0 && (
                      <div>
                        <h6 className="fw-semibold text-gray-700 mb-3">פירוט לפי קמפיין ({reschedulePreview.totalCampaigns})</h6>
                        <div className="d-flex flex-column gap-3">
                          {reschedulePreview.campaigns.map((campaign: any) => (
                            <div key={campaign._id} className="border rounded-3 overflow-hidden" style={{ borderLeftWidth: '4px', borderLeftColor: '#6366f1' }}>
                              {/* Campaign Header */}
                              <div className="p-3 border-bottom" style={{ backgroundColor: '#f9fafb' }}>
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="fw-semibold text-gray-800">{campaign.name}</span>
                                    {campaign.warnings && campaign.warnings.length > 0 && !campaign.warnings.some((w: any) => w.type === 'campaign_limit') && (
                                      <span className="text-warning cursor-pointer" title={campaign.warnings.map((w: any) => w.message).join('\n')}>⚠️</span>
                                    )}
                                  </div>
                                  <div className="d-flex gap-2 fs-7">
                                    <span className="fw-semibold" style={{ color: '#16a34a' }}>+{campaign.estimatedNew || 0} פוסטים</span>
                                    {campaign.joinGroupsNeeded > 0 && (
                                      <div className="d-flex align-items-center gap-2">
                                        <span className="fw-semibold" style={{ color: '#d97706' }}>+{campaign.joinGroupsNeeded} הצטרפות</span>
                                        <input
                                          type="number"
                                          className="form-control form-control-sm"
                                          style={{ width: '60px', height: '26px', fontSize: '12px' }}
                                          value={joinCampaignGroupsInput[campaign._id] ?? campaign.joinGroupsNeeded}
                                          onChange={(e) => setJoinCampaignGroupsInput(prev => ({ ...prev, [campaign._id]: e.target.value }))}
                                          min={1}
                                        />
                                        <button
                                          className="btn btn-sm btn-warning py-0 px-2"
                                          style={{ fontSize: '11px', height: '26px' }}
                                          disabled={joiningCampaignGroups === campaign._id}
                                          onClick={async () => {
                                            setJoiningCampaignGroups(campaign._id)
                                            try {
                                              const maxJoin = joinCampaignGroupsInput[campaign._id] ? parseInt(joinCampaignGroupsInput[campaign._id]) : undefined
                                              const result = await joinCampaignGroups(campaign._id, maxJoin)
                                              if (result.success) {
                                                setRescheduleResult(`נוצרו ${result.joinGroupsCreated} הצטרפויות לקבוצות עבור ${campaign.name}`)
                                              } else {
                                                setRescheduleResult(`שגיאה: ${result.error}`)
                                              }
                                            } catch (err: any) {
                                              setRescheduleResult(`שגיאה: ${err.message}`)
                                            } finally {
                                              setJoiningCampaignGroups(null)
                                            }
                                          }}
                                        >
                                          {joiningCampaignGroups === campaign._id ? '...' : 'הצטרף לקבוצות'}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="d-flex gap-3 fs-8 text-muted">
                                  <span>{campaign.profilesCount || 0} פרופילים</span>
                                  <span>{campaign.groupsCount || 0} קבוצות</span>
                                </div>
                              </div>

                              {/* Campaign Limit Warning */}
                              {campaign.warnings && campaign.warnings.some((w: any) => w.type === 'campaign_limit') && (
                                <div className="p-3 d-flex align-items-center gap-2" style={{ backgroundColor: '#fef3c7', borderBottom: '1px solid #f59e0b' }}>
                                  <i className="bi bi-exclamation-triangle-fill fs-4" style={{ color: '#d97706' }}></i>
                                  <div>
                                    <div className="fw-bold" style={{ color: '#92400e' }}>הקמפיין הגיע למכסה היומית ({campaign.maxDaily} פוסטים)</div>
                                    <div className="fs-8" style={{ color: '#92400e' }}>הפרופילים יכולים לפרסם יותר, אבל מגבלת הקמפיין עוצרת אותם. הגדל את המכסה בהגדרות הקמפיין.</div>
                                  </div>
                                </div>
                              )}

                              {/* Profile Usage */}
                              {campaign.profileUsage && campaign.profileUsage.length > 0 && (
                                <div className="p-3">
                                  <div className="fs-8 fw-medium text-muted mb-2">פרופילים וקבוצות:</div>
                                  <div className="d-flex flex-wrap gap-3">
                                    {campaign.profileUsage.map((profile: any) => {
                                      const usagePercent = Math.round((profile.posted_today / profile.max_daily) * 100);
                                      const notInAllGroups = profile.groups_joined < profile.total_groups;
                                      const postsToAdd = profile.posts_to_add || 0;
                                      const groupsJoined = profile.groups_joined || 0;
                                      const totalGroups = profile.total_groups || 0;
                                      return (
                                        <div
                                          key={profile._id}
                                          className="d-flex align-items-center gap-2 p-2 rounded-2"
                                          style={{
                                            backgroundColor: profile.at_limit ? '#fef2f2' : '#f9fafb',
                                            border: `1px solid ${profile.at_limit ? '#fecaca' : '#e5e7eb'}`,
                                            opacity: profile.at_limit ? 0.7 : 1
                                          }}
                                        >
                                          {/* Profile avatar */}
                                          <div className="position-relative">
                                            {profile.facebook_id ? (
                                              <img
                                                src={`https://graph.facebook.com/${profile.facebook_id}/picture?type=small&access_token=${FB_ACCESS_TOKEN}`}
                                                alt={profile.name}
                                                className="rounded-circle"
                                                style={{ width: 28, height: 28, objectFit: 'cover' }}
                                                onError={(e) => {
                                                  (e.target as HTMLImageElement).style.display = 'none';
                                                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove('d-none');
                                                }}
                                              />
                                            ) : null}
                                            <div
                                              className={`rounded-circle d-flex align-items-center justify-content-center text-white fs-9 fw-bold ${profile.facebook_id ? 'd-none' : ''}`}
                                              style={{ width: 28, height: 28, background: 'linear-gradient(to bottom right, #3b82f6, #8b5cf6)' }}
                                            >
                                              {profile.name?.charAt(0) || '?'}
                                            </div>
                                          </div>
                                          {/* Profile info */}
                                          <div className="d-flex flex-column">
                                            <span className="fs-9 fw-medium text-gray-800">{profile.name}</span>
                                            <div className="d-flex gap-2 fs-9">
                                              {/* Groups membership */}
                                              <span style={{ color: groupsJoined === totalGroups ? '#16a34a' : '#d97706' }}>
                                                {groupsJoined}/{totalGroups} קבוצות
                                              </span>
                                              {/* Posts info */}
                                              {profile.at_limit ? (
                                                <span style={{ color: '#ef4444' }}>במכסה</span>
                                              ) : postsToAdd > 0 ? (
                                                <span style={{ color: '#16a34a' }}>+{postsToAdd} פוסטים</span>
                                              ) : (
                                                <span className="text-muted">{profile.remaining} נותרו</span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Profile Join Groups Section */}
                    {reschedulePreview.profileJoinGroups && reschedulePreview.profileJoinGroups.length > 0 && (
                      <div className="mt-4">
                        <h6 className="fw-semibold text-gray-700 mb-3">הצטרפות לקבוצות לפי פרופיל</h6>
                        <div className="d-flex flex-wrap gap-3">
                          {reschedulePreview.profileJoinGroups.map((profile: any) => (
                            <div
                              key={profile._id}
                              className="d-flex align-items-center gap-2 p-2 rounded-3"
                              style={{ backgroundColor: '#fef3c7', border: '1px solid #fcd34d', minWidth: '220px' }}
                            >
                              {profile.facebook_id ? (
                                <img
                                  src={`https://graph.facebook.com/${profile.facebook_id}/picture?type=small&access_token=${FB_ACCESS_TOKEN}`}
                                  alt={profile.name}
                                  className="rounded-circle"
                                  style={{ width: 28, height: 28, objectFit: 'cover' }}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div
                                  className="rounded-circle d-flex align-items-center justify-content-center text-white fs-9 fw-bold"
                                  style={{ width: 28, height: 28, background: 'linear-gradient(to bottom right, #3b82f6, #8b5cf6)' }}
                                >
                                  {profile.name?.charAt(0) || '?'}
                                </div>
                              )}
                              <div className="fs-8 flex-grow-1">
                                <div className="fw-medium">{profile.name}</div>
                                <div className="d-flex align-items-center gap-2 mt-1">
                                  <span className="text-muted">מקס׳ יומי:</span>
                                  <input
                                    type="number"
                                    min="0"
                                    max="50"
                                    defaultValue={profile.max_join_groups || 7}
                                    className="form-control form-control-sm"
                                    style={{ width: '60px', padding: '2px 6px', fontSize: '12px' }}
                                    onChange={async (e) => {
                                      const newValue = parseInt(e.target.value) || 0;
                                      try {
                                        await updateMaxJoinGroups(profile._id, newValue);
                                      } catch (err) {
                                        console.error('Failed to update max join groups:', err);
                                      }
                                    }}
                                  />
                                  <span className="fw-semibold" style={{ color: '#d97706' }}>
                                    ({profile.groups_to_join_today} היום)
                                  </span>
                                </div>
                                {profile.groups_remaining > 0 && (
                                  <div className="text-muted fs-9" title={`עוד ${profile.groups_remaining} קבוצות בימים הבאים`}>
                                    +{profile.groups_remaining} קבוצות נוספות
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Warning Message */}
                    <div className="rounded-3 p-3 mt-4" style={{ backgroundColor: '#fefce8', border: '1px solid #fef08a' }}>
                      <div className="d-flex align-items-start gap-2">
                        <span style={{ color: '#ca8a04', fontSize: 20 }}>⚠️</span>
                        <div className="fs-7" style={{ color: '#854d0e' }}>
                          פעולה זו תמחק את כל הפוסטים המתוזמנים שטרם פורסמו ותיצור תזמון חדש. פוסטים שכבר פורסמו לא יושפעו.
                        </div>
                      </div>
                    </div>

                    {/* Result message */}
                    {rescheduleResult && (
                      <div className={`alert ${rescheduleResult.includes('הושלם') ? 'alert-success' : 'alert-danger'} mt-4`}>
                        <i className={`bi ${rescheduleResult.includes('הושלם') ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-2`}></i>
                        {rescheduleResult}
                      </div>
                    )}
                  </div>
                ) : rescheduleResult ? (
                  <div className="alert alert-danger">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {rescheduleResult}
                  </div>
                ) : null}
              </div>

              {/* Footer */}
              <div className="border-top px-4 py-3 d-flex gap-2 justify-content-start" style={{ backgroundColor: '#f9fafb' }}>
                {reschedulePreview && !rescheduleResult?.includes('הושלם') && (
                  <button
                    type="button"
                    className="btn text-white px-4 d-flex align-items-center gap-2"
                    style={{ background: 'linear-gradient(to left, #f97316, #ef4444)' }}
                    onClick={handleRescheduleAllCampaigns}
                    disabled={rescheduling}
                  >
                    {rescheduling ? (
                      <>
                        <span className="spinner-border spinner-border-sm"></span>
                        <span>מריץ תזמון...</span>
                      </>
                    ) : (
                      <>
                        <span>🔄</span>
                        <span>אשר והרץ תזמון</span>
                      </>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-light px-4"
                  onClick={closeRescheduleModal}
                >
                  ביטול
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Tooltip Styles */}
      <style>{`
        .slot-item {
          position: relative;
        }
        .slot-tooltip {
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: white;
          border: 1px solid #e4e6ef;
          border-radius: 6px;
          padding: 8px 12px;
          min-width: 180px;
          box-shadow: 0 0 20px 0 rgba(0,0,0,0.15);
          opacity: 0;
          visibility: hidden;
          transition: all 0.2s ease;
          z-index: 100;
          margin-bottom: 8px;
          text-align: center;
        }
        .slot-tooltip::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 6px solid transparent;
          border-top-color: white;
        }
        .slot-item:hover .slot-tooltip {
          opacity: 1;
          visibility: visible;
        }
      `}</style>
    </>
  )
}

export default TodaySchedule
